import React, { useState, useMemo, useEffect } from "react";
import { GitMerge, Search, AlertTriangle, CheckCircle2, RefreshCw, FileCheck2, Sparkles, ChevronRight, CalendarX, Building2, Hash, Link2, RotateCcw, Unlink, Upload, FileSpreadsheet, Trash2 } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";
import { fmtDate } from "../lib/format";

/**
 * Settings → Dedupe tab.
 * Tools:
 *   1. Equipment merger — strict (customer + serial + part #) OR
 *      loose (any 2 of Asset / Serial / Part match within customer).
 *      The duplicates' calibration history / certificates / job-items
 *      get re-pointed to the master, then the duplicate rows are deleted.
 *   2. Certificate dedupe — same (equipment + cal date + CR # + part #).
 *   3. Calibration event dedupe — same (equipment + cal date).
 *      Keep the oldest in each cluster, delete the rest.
 */
export default function DedupeTab() {
  const [mode, setMode] = useState("equipment");
  return (
    <>
      <div className="ftabs" style={{ marginTop: 6 }}>
        <button className={`ftab ${mode === "equipment" ? "active" : ""}`}
                onClick={() => setMode("equipment")} data-testid="dedupe-mode-equipment">
          <GitMerge size={13} style={{ verticalAlign: "-2px", marginRight: 5 }} />
          Equipment duplicates
        </button>
        <button className={`ftab ${mode === "certificates" ? "active" : ""}`}
                onClick={() => setMode("certificates")} data-testid="dedupe-mode-certificates">
          <FileCheck2 size={13} style={{ verticalAlign: "-2px", marginRight: 5 }} />
          Certificate duplicates
        </button>
        <button className={`ftab ${mode === "calevents" ? "active" : ""}`}
                onClick={() => setMode("calevents")} data-testid="dedupe-mode-calevents">
          <CalendarX size={13} style={{ verticalAlign: "-2px", marginRight: 5 }} />
          Calibration date duplicates
        </button>
        <button className={`ftab ${mode === "customers" ? "active" : ""}`}
                onClick={() => setMode("customers")} data-testid="dedupe-mode-customers">
          <Building2 size={13} style={{ verticalAlign: "-2px", marginRight: 5 }} />
          Customer duplicates
        </button>
        <button className={`ftab ${mode === "jobs" ? "active" : ""}`}
                onClick={() => setMode("jobs")} data-testid="dedupe-mode-jobs">
          <Link2 size={13} style={{ verticalAlign: "-2px", marginRight: 5 }} />
          Job / DN duplicates
        </button>
        <button className={`ftab ${mode === "cleanup" ? "active" : ""}`}
                onClick={() => setMode("cleanup")} data-testid="dedupe-mode-cleanup">
          <Sparkles size={13} style={{ verticalAlign: "-2px", marginRight: 5 }} />
          Cleanup wizard
        </button>
      </div>

      {mode === "equipment" && <EquipmentDedupe />}
      {mode === "certificates" && <CertificateDedupe />}
      {mode === "calevents" && <CalEventDedupe />}
      {mode === "customers" && <CustomerDedupe />}
      {mode === "jobs" && <JobDedupe />}
      {mode === "cleanup" && (
        <>
          <CaspioWipImporterCard />
          <SeedActiveLabCard />
          <FixWipSeedGapsCard />
          <WipSeedDuplicatesCard />
          <CrossBatchDuplicatesCard />
          <TechnicianDedupeCard />
          <CleanupWizard />
        </>
      )}
    </>
  );
}

// ============================================================
//  Equipment dedupe
// ============================================================
function CustomerPickerInline({ cluster, value, onChange, testidBase }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);

  React.useEffect(() => {
    if (q.length < 2) { setResults([]); return; }
    const t = setTimeout(() => api.get("/customers", { params: { search: q, limit: 50 } })
      .then((r) => setResults(r.data.items || [])).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div style={{ position: "relative", minWidth: 230 }}>
      <label style={{ fontSize: 11, color: "var(--text-2)", display: "block", marginBottom: 3 }}>Merge into customer <span className="req">*</span></label>
      {!open ? (
        <button type="button" className="input" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", cursor: "pointer" }}
                onClick={() => { setOpen(true); setQ(""); setResults([]); }} data-testid={`${testidBase}-pick`}>
          <span className={value ? "t-strong" : "t-mut"} style={{ fontSize: 12.5 }}>{value ? value.customer_name : "Choose customer…"}</span>
          <Search size={12} color="var(--text-3)" />
        </button>
      ) : (
        <>
          <input className="input" autoFocus placeholder="Search any customer…" value={q}
                 onChange={(e) => setQ(e.target.value)} onBlur={() => setTimeout(() => setOpen(false), 200)}
                 data-testid={`${testidBase}-search`} />
          {results.length > 0 && (
            <div className="card" style={{ position: "absolute", zIndex: 20, top: 62, left: 0, right: 0, maxHeight: 220, overflowY: "auto" }}>
              {results.map((c) => (
                <button key={c.id} type="button" className="nav-item" style={{ color: "var(--text)", borderRadius: 0, gap: 8 }}
                        onClick={() => { onChange({ id: c.id, customer_name: c.customer_name }); setOpen(false); }}
                        data-testid={`${testidBase}-opt-${c.id}`}>
                  {c.customer_number && <span className="mono" style={{ color: "var(--gold-deep)", fontSize: 11, minWidth: 55 }}>{c.customer_number}</span>}
                  <span>{c.customer_name}</span>
                </button>
              ))}
            </div>
          )}
        </>
      )}
      {cluster.distinct_customers?.length > 0 && (
        <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
          {cluster.distinct_customers.map((c) => (
            <button key={c.id} type="button" className="btn ghost sm"
                    style={{ fontSize: 11, padding: "3px 8px", borderColor: value?.id === c.id ? "var(--gold-deep)" : undefined,
                             color: value?.id === c.id ? "var(--gold-deep)" : undefined }}
                    onClick={() => onChange({ id: c.id, customer_name: c.customer_name })}
                    data-testid={`${testidBase}-quick-${c.id}`}>
              {c.customer_name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function EquipmentDedupe() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyCluster, setBusyCluster] = useState(null);
  const [selected, setSelected] = useState({});  // { masterId: true }
  const [bulkBusy, setBulkBusy] = useState(false);
  const [matchMode, setMatchMode] = useState("strict");
  const [crossCustomer, setCrossCustomer] = useState(false);
  const [targetCustomer, setTargetCustomer] = useState({}); // { masterId: {id, customer_name} }

  const scan = async () => {
    setLoading(true);
    setSelected({});
    try {
      const params = { limit: 500, mode: matchMode };
      if (matchMode === "loose" && crossCustomer) params.cross_customer = true;
      const r = await api.get("/dedupe/equipment", { params });
      setData(r.data);
      if (r.data.cross_customer) {
        const defaults = {};
        r.data.clusters.forEach((cl) => {
          if (cl.master.customer_id) {
            defaults[cl.master.id] = { id: cl.master.customer_id, customer_name: cl.master.customer_name };
          }
        });
        setTargetCustomer(defaults);
      } else {
        setTargetCustomer({});
      }
      if (r.data.total_clusters === 0) toast.success("No duplicate equipment found — clean register.");
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  const toggleAll = () => {
    if (!data) return;
    const allSelected = data.clusters.length > 0 && data.clusters.every((c) => selected[c.master.id]);
    if (allSelected) setSelected({});
    else {
      const next = {};
      data.clusters.forEach((c) => { next[c.master.id] = true; });
      setSelected(next);
    }
  };
  const toggleOne = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));

  const selectedClusters = useMemo(
    () => (data?.clusters || []).filter((c) => selected[c.master.id]),
    [data, selected]
  );
  const selectedDupCount = useMemo(
    () => selectedClusters.reduce((acc, c) => acc + c.duplicates.length, 0),
    [selectedClusters]
  );
  const missingTargetCount = useMemo(
    () => (data?.cross_customer ? selectedClusters.filter((c) => !targetCustomer[c.master.id]).length : 0),
    [data, selectedClusters, targetCustomer]
  );
  // Cross-customer mode: bring clusters still missing a target customer to
  // the top so they're easy to find and fix, instead of hunting through
  // potentially hundreds of already-defaulted clusters (2026-07-23 request).
  const displayClusters = useMemo(() => {
    const list = data?.clusters || [];
    if (!data?.cross_customer) return list;
    return [...list].sort((a, b) => {
      const aMissing = !targetCustomer[a.master.id];
      const bMissing = !targetCustomer[b.master.id];
      return aMissing === bMissing ? 0 : aMissing ? -1 : 1;
    });
  }, [data, targetCustomer]);

  const mergeBulk = async () => {
    if (!selectedClusters.length) return;
    const isCross = !!data?.cross_customer;
    if (isCross && selectedClusters.some((c) => !targetCustomer[c.master.id])) {
      toast.error("Every selected cluster needs a target customer chosen first.");
      return;
    }
    if (!window.confirm(
      `Merge ${selectedClusters.length} cluster${selectedClusters.length === 1 ? "" : "s"} ` +
      `(${selectedDupCount} duplicate row${selectedDupCount === 1 ? "" : "s"}) into their masters?\n\n` +
      `All calibration events, certificates and job-line items on the duplicate rows will be moved to the master row of each cluster` +
      (isCross ? `, the master's customer will be set to your chosen target customer,` : "") +
      ` then the duplicates will be deleted. This is fully audited. Cannot be undone.`
    )) return;
    setBulkBusy(true);
    try {
      const payload = {
        clusters: selectedClusters.map((c) => ({
          master_id: c.master.id,
          duplicate_ids: c.duplicates.map((d) => d.id),
          target_customer_id: isCross ? targetCustomer[c.master.id]?.id : undefined,
        })),
      };
      const r = await api.post("/dedupe/equipment/merge-bulk", payload);
      const m = r.data;
      toast.success(
        `${m.clusters_merged} cluster(s) merged · ${m.duplicates_deleted} dup(s) deleted · ` +
        `${m.certs_moved} cert(s) · ${m.events_moved} event(s) moved` +
        (m.clusters_failed ? ` · ${m.clusters_failed} failed` : "")
      );
      if (m.errors?.length) console.warn("Merge errors:", m.errors);
      const mergedIds = new Set(selectedClusters.map((c) => c.master.id));
      setData((d) => ({
        ...d,
        clusters: d.clusters.filter((c) => !mergedIds.has(c.master.id)),
        total_clusters: Math.max(0, (d.total_clusters || 0) - m.clusters_merged),
        total_duplicate_rows: Math.max(0, (d.total_duplicate_rows || 0) - m.duplicates_deleted),
      }));
      setSelected({});
    } catch (e) { toast.error(apiErr(e)); } finally { setBulkBusy(false); }
  };

  const merge = async (cluster) => {
    const dupIds = cluster.duplicates.map((d) => d.id);
    const target = targetCustomer[cluster.master.id];
    if (data?.cross_customer && !target) { toast.error("Choose which customer this merged equipment should belong to first."); return; }
    const confirmMsg = data?.cross_customer
      ? `Merge ${dupIds.length} duplicate${dupIds.length === 1 ? "" : "s"} onto the master and set the customer to "${target.customer_name}"?\n\n` +
        `Master: ${cluster.master.description || "—"} · SN ${cluster.master.serial_number || "—"}\n\n` +
        `All calibration events, certificates and job-line items linked to the duplicate rows will be moved to the master, then the duplicate equipment rows will be deleted. This cannot be undone (but is fully audited).`
      : `Merge ${dupIds.length} duplicate${dupIds.length === 1 ? "" : "s"} onto the master?\n\n` +
        `Master: ${cluster.master.description || "—"} · SN ${cluster.master.serial_number || "—"} (${cluster.master.customer_name})\n\n` +
        `All calibration events, certificates and job-line items linked to the duplicate rows will be moved to the master, then the duplicate equipment rows will be deleted. This cannot be undone (but is fully audited).`;
    if (!window.confirm(confirmMsg)) return;
    const key = cluster.master.id;
    setBusyCluster(key);
    try {
      const r = data?.cross_customer
        ? await api.post("/dedupe/equipment/merge-cross-customer", {
            master_id: cluster.master.id, duplicate_ids: dupIds, target_customer_id: target.id,
          })
        : await api.post("/dedupe/equipment/merge", {
            master_id: cluster.master.id, duplicate_ids: dupIds,
          });
      toast.success(
        `Merged ${r.data.duplicates_deleted} duplicate(s) · ` +
        `${r.data.events_moved} event(s) · ${r.data.certs_moved} cert(s) · ${r.data.job_items_moved} job-line(s) moved`
      );
      setData((d) => ({
        ...d,
        clusters: d.clusters.filter((c) => c.master.id !== key),
        total_clusters: Math.max(0, (d.total_clusters || 1) - 1),
        total_duplicate_rows: Math.max(0, (d.total_duplicate_rows || 0) - dupIds.length),
      }));
      setSelected((s) => { const n = { ...s }; delete n[key]; return n; });
    } catch (e) { toast.error(apiErr(e)); } finally { setBusyCluster(null); }
  };

  const allSelected = data?.clusters?.length > 0 && data.clusters.every((c) => selected[c.master.id]);

  return (
    <div className="card pad" style={{ marginTop: 16 }}>
      <div className="sec-h">
        <h3>Equipment duplicates {matchMode === "loose" ? "— loose match (any 2 of Asset / SN / Part)" : "— strict (Serial + Part #)"}</h3>
        <GitMerge size={18} color="var(--gold-deep)" />
      </div>
      <p style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 10 }}>
        {matchMode === "loose"
          ? <>Clusters equipment records where <b>any two</b> of (Asset #, Serial #, Part #) match{crossCustomer ? "" : " within the same customer"}. Catches typo-only mismatches between historical Caspio rows. The richest row (active, most cal history, latest updates) is suggested as the <b>master</b>; all other rows in the cluster are re-pointed onto it and deleted. Read-only until you click <b>Merge</b>.</>
          : <>Identifies equipment records that represent the same physical asset (same customer, same serial number, same part number). The richest row (active, most calibration history, latest updates) is suggested as the <b>master</b>; all other rows in the cluster are re-pointed onto it and deleted. Read-only until you click <b>Merge</b>.</>}
      </p>

      <div className="ftabs" style={{ marginBottom: 12 }}>
        <button className={`ftab ${matchMode === "strict" ? "active" : ""}`}
                onClick={() => { setMatchMode("strict"); setData(null); setSelected({}); }}
                data-testid="dedupe-eq-mode-strict">
          Strict (SN + Part)
        </button>
        <button className={`ftab ${matchMode === "loose" ? "active" : ""}`}
                onClick={() => { setMatchMode("loose"); setData(null); setSelected({}); }}
                data-testid="dedupe-eq-mode-loose">
          Loose (any 2 of Asset / SN / Part)
        </button>
      </div>

      {matchMode === "loose" && (
        <label className="notice" style={{ background: "var(--card-2)", marginBottom: 14, cursor: "pointer", display: "flex", alignItems: "flex-start", gap: 10 }}>
          <input type="checkbox" checked={crossCustomer}
                 onChange={(e) => { setCrossCustomer(e.target.checked); setData(null); setSelected({}); }}
                 style={{ marginTop: 2 }} data-testid="dedupe-eq-cross-customer-toggle" />
          <span>
            <b>Include cross-customer matches.</b> Also finds the same physical asset registered under a <b>different</b> customer (e.g. a duplicated/ghost customer row) — not just within one customer. You'll pick which customer each merged record should belong to before merging.
          </span>
        </label>
      )}

      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
        <button className="btn gold" onClick={scan} disabled={loading} data-testid="dedupe-eq-scan">
          {loading ? <><RefreshCw size={14} className="spin" /> Scanning…</> : <><Search size={14} /> Scan for duplicates</>}
        </button>
        {data && data.clusters.length > 0 && (
          <>
            <button className="btn ghost sm" onClick={toggleAll} data-testid="dedupe-eq-toggle-all">
              {allSelected ? "Deselect all" : `Select all (${data.clusters.length})`}
            </button>
            <button className="btn gold sm" onClick={mergeBulk}
                    disabled={bulkBusy || selectedClusters.length === 0 || missingTargetCount > 0}
                    title={missingTargetCount > 0 ? `Choose a target customer for ${missingTargetCount} selected cluster(s) first` : undefined}
                    data-testid="dedupe-eq-merge-selected">
              <GitMerge size={13} /> {bulkBusy
                ? `Merging ${selectedClusters.length}…`
                : `Merge ${selectedClusters.length} selected → ${selectedDupCount} dup${selectedDupCount === 1 ? "" : "s"}`}
            </button>
            {missingTargetCount > 0 && (
              <span className="t-mut" style={{ fontSize: 12, color: "var(--red)" }}>
                {missingTargetCount} selected cluster(s) still need a target customer chosen below
              </span>
            )}
          </>
        )}
        {data && (
          <span className="t-mut" style={{ fontSize: 13, marginLeft: "auto" }}>
            <b style={{ color: "var(--text-1)" }}>{data.total_clusters}</b> cluster(s) · <b style={{ color: data.total_duplicate_rows ? "var(--red)" : "var(--text-1)" }}>{data.total_duplicate_rows}</b> duplicate row(s)
          </span>
        )}
      </div>

      {data && data.clusters.length === 0 && (
        <div className="notice" style={{ background: "var(--card-2)" }}>
          <CheckCircle2 size={15} color="var(--green)" />
          <span>No duplicate equipment found. Your register is clean on this rule.</span>
        </div>
      )}

      {displayClusters.map((cl) => {
        const isSelected = !!selected[cl.master.id];
        const isCross = !!data.cross_customer;
        const missingTarget = isCross && !targetCustomer[cl.master.id];
        return (
        <div key={cl.master.id} className="card pad" data-testid={missingTarget ? `dedupe-eq-missing-target-${cl.master.id}` : undefined}
             style={{ marginBottom: 12, background: "var(--card-2)",
                      borderLeft: missingTarget ? "4px solid var(--amber)" : `3px solid ${isCross ? "var(--red)" : isSelected ? "var(--gold-deep)" : "var(--line)"}` }}>
          {missingTarget && (
            <div className="badge b-amber" style={{ marginBottom: 8, fontSize: 11 }}>
              <AlertTriangle size={11} /> Needs a target customer — see picker below
            </div>
          )}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 8 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", flex: 1 }}>
              <input type="checkbox" checked={isSelected}
                     onChange={() => toggleOne(cl.master.id)}
                     data-testid={`dedupe-eq-check-${cl.master.id}`}
                     style={{ width: 16, height: 16, cursor: "pointer" }} />
              <span>
                <span className="t-strong">{cl.master.description || "—"}</span>
                <span className="mono t-mut" style={{ marginLeft: 8 }}>SN {cl.master.serial_number || "—"} · Asset {cl.master.asset_number || "—"} · P/N {cl.master.part_number || "—"}</span>
                {!isCross && <span className="t-mut" style={{ marginLeft: 8, fontSize: 12 }}>· {cl.master.customer_name}</span>}
                {isCross && (
                  <span className="badge b-red" style={{ marginLeft: 8 }}><span className="bd" />Spans {cl.distinct_customers?.length || 0} customers</span>
                )}
              </span>
            </label>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 10 }}>
              {isCross && (
                <CustomerPickerInline cluster={cl} value={targetCustomer[cl.master.id]}
                  onChange={(v) => setTargetCustomer((s) => ({ ...s, [cl.master.id]: v }))}
                  testidBase={`dedupe-eq-cross-cust-${cl.master.id}`} />
              )}
              <button className="btn sm gold" onClick={() => merge(cl)}
                      disabled={busyCluster === cl.master.id || bulkBusy || (isCross && !targetCustomer[cl.master.id])}
                      data-testid={`dedupe-eq-merge-${cl.master.id}`}>
                <GitMerge size={12} /> {busyCluster === cl.master.id ? "Merging…" : `Merge ${cl.duplicates.length} into master`}
              </button>
            </div>
          </div>

          <div className="tbl-wrap">
            <table>
              <thead><tr><th style={{ width: 60 }}>Role</th>{isCross && <th>Customer</th>}<th>Serial</th><th>Asset</th><th>Part #</th><th>Match</th><th>Last cal.</th><th>Cal due</th><th>Last cert</th><th>Cal history</th><th>Open jobs</th><th>Updated</th><th>Scrapped</th></tr></thead>
              <tbody>
                <tr style={{ background: "rgba(212,175,55,.08)" }}>
                  <td><span className="badge b-gold"><span className="bd" />Master</span></td>
                  {isCross && <td className="t-strong">{cl.master.customer_name || "—"}</td>}
                  <td className="mono">{cl.master.serial_number || "—"}</td>
                  <td className="mono">{cl.master.asset_number || "—"}</td>
                  <td className="mono">{cl.master.part_number || "—"}</td>
                  <td className="t-mut">—</td>
                  <td className="mono">{fmtDate(cl.master.last_calibration_date)}</td>
                  <td className="mono">{fmtDate(cl.master.calibration_due_date)}</td>
                  <td className="mono">{cl.master.last_certificate_number || "—"}</td>
                  <td className="mono t-strong">{cl.master.certs} certs · {cl.master.events} events</td>
                  <td className="mono">{cl.master.job_items}</td>
                  <td className="mono t-mut">{fmtDate(cl.master.updated_at)}</td>
                  <td>{cl.master.scrapped ? "yes" : "—"}</td>
                </tr>
                {cl.duplicates.map((d) => (
                  <tr key={d.id}>
                    <td><span className="badge b-red"><span className="bd" />Dup</span></td>
                    {isCross && <td className={d.customer_id !== cl.master.customer_id ? "t-strong" : ""} style={d.customer_id !== cl.master.customer_id ? { color: "var(--red)" } : undefined}>{d.customer_name || "—"}</td>}
                    <td className="mono">{d.serial_number || "—"}</td>
                    <td className="mono">{d.asset_number || "—"}</td>
                    <td className="mono">{d.part_number || "—"}</td>
                    <td style={{ fontSize: 11, color: "var(--text-2)" }}>{(d.match_rules || []).join(", ") || "—"}</td>
                    <td className="mono">{fmtDate(d.last_calibration_date)}</td>
                    <td className="mono">{fmtDate(d.calibration_due_date)}</td>
                    <td className="mono">{d.last_certificate_number || "—"}</td>
                    <td className="mono">{d.certs} certs · {d.events} events</td>
                    <td className="mono">{d.job_items}</td>
                    <td className="mono t-mut">{fmtDate(d.updated_at)}</td>
                    <td>{d.scrapped ? "yes" : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );})}
    </div>
  );
}

// ============================================================
//  Certificate dedupe
// ============================================================
function CertificateDedupe() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const scan = async () => {
    setLoading(true);
    try {
      const r = await api.get("/dedupe/certificates", { params: { limit: 500 } });
      setData(r.data);
      if (r.data.total_clusters === 0) toast.success("No duplicate certificates found.");
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  const applyAll = async () => {
    if (!data || !data.clusters.length) return;
    if (!window.confirm(
      `Delete ${data.total_duplicate_rows} duplicate certificate row(s) across ${data.clusters.length} cluster(s)?\n\n` +
      `The OLDEST certificate in each cluster (matched on equipment + cal date + CR # + part #) is kept; the rest are deleted. This is audited and cannot be undone.`
    )) return;
    setBusy(true);
    try {
      const cluster_keys = data.clusters.map((c) => c.key);
      const r = await api.post("/dedupe/certificates/apply", { cluster_keys });
      toast.success(`${r.data.certs_deleted} duplicate certificate(s) removed across ${r.data.clusters_processed} cluster(s)`);
      setData(null);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="card pad" style={{ marginTop: 16 }}>
      <div className="sec-h">
        <h3>Certificate duplicates — same equipment + cal date + CR # + part #</h3>
        <FileCheck2 size={18} color="var(--gold-deep)" />
      </div>
      <p style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 14 }}>
        Looks for certificates that are exact functional duplicates — same equipment, same calibration date, same CR number, same part number. Keeps the earliest record (by created_at) in each cluster and deletes the rest.
      </p>

      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
        <button className="btn gold" onClick={scan} disabled={loading} data-testid="dedupe-cert-scan">
          {loading ? <><RefreshCw size={14} className="spin" /> Scanning…</> : <><Search size={14} /> Scan for duplicates</>}
        </button>
        {data && data.clusters.length > 0 && (
          <button className="btn" onClick={applyAll} disabled={busy} data-testid="dedupe-cert-apply"
                  style={{ background: "#a32424", color: "#fff", borderColor: "#a32424" }}>
            <AlertTriangle size={14} /> {busy ? "Removing…" : `Remove ${data.total_duplicate_rows} duplicate(s)`}
          </button>
        )}
        {data && (
          <span className="t-mut" style={{ fontSize: 13 }}>
            <b style={{ color: "var(--text-1)" }}>{data.total_clusters}</b> cluster(s) · <b style={{ color: data.total_duplicate_rows ? "var(--red)" : "var(--text-1)" }}>{data.total_duplicate_rows}</b> duplicate cert row(s)
          </span>
        )}
      </div>

      {data && data.clusters.length === 0 && (
        <div className="notice" style={{ background: "var(--card-2)" }}>
          <CheckCircle2 size={15} color="var(--green)" />
          <span>No duplicate certificates found.</span>
        </div>
      )}

      {data?.clusters?.length > 0 && (
        <div className="tbl-wrap">
          <table>
            <thead><tr><th>Customer</th><th>Equipment</th><th>SN / P/N</th><th>Cal date</th><th>CR #</th><th>Master created</th><th>Dups to delete</th></tr></thead>
            <tbody>
              {data.clusters.map((cl, i) => (
                <tr key={cl.master?._id || cl.master?.id || `${cl.key?.certificate_number || ""}-${cl.key?.calibration_date || ""}-${i}`}>
                  <td className="t-strong">{cl.equipment?.customer_name || "—"}</td>
                  <td>{cl.equipment?.description || "—"}</td>
                  <td className="mono t-mut">{cl.equipment?.serial_number || "—"} · {cl.equipment?.part_number || "—"}</td>
                  <td className="mono">{cl.key.calibration_date}</td>
                  <td className="mono">{cl.key.certificate_number}</td>
                  <td className="mono t-mut">{fmtDate(cl.master.created_at)}</td>
                  <td className="mono" style={{ color: "var(--red)", fontWeight: 700 }}>{cl.duplicates.length}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ============================================================
//  Calibration event dedupe — same equipment + same cal date
// ============================================================
function CalEventDedupe() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const scan = async () => {
    setLoading(true);
    try {
      const r = await api.get("/dedupe/calibration-events", { params: { limit: 1000 } });
      setData(r.data);
      if (r.data.total_clusters === 0) toast.success("No duplicate calibration dates found.");
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  const applyAll = async () => {
    if (!data || !data.clusters.length) return;
    if (!window.confirm(
      `Delete ${data.total_duplicate_rows} duplicate calibration event row(s) across ${data.clusters.length} cluster(s)?\n\n` +
      `The OLDEST event in each (equipment + calibration date) cluster is kept; the rest are deleted. This is audited and cannot be undone.`
    )) return;
    setBusy(true);
    try {
      const cluster_keys = data.clusters.map((c) => c.key);
      const r = await api.post("/dedupe/calibration-events/apply", { cluster_keys });
      toast.success(`${r.data.events_deleted} duplicate event(s) removed across ${r.data.clusters_processed} cluster(s)`);
      setData(null);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="card pad" style={{ marginTop: 16 }}>
      <div className="sec-h">
        <h3>Calibration date duplicates — same equipment + same cal date</h3>
        <CalendarX size={18} color="var(--gold-deep)" />
      </div>
      <p style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 14 }}>
        Looks for calibration_event rows that share the exact same equipment + calibration date. Keeps the oldest row (by created_at) in each cluster and deletes the rest.
      </p>

      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
        <button className="btn gold" onClick={scan} disabled={loading} data-testid="dedupe-calev-scan">
          {loading ? <><RefreshCw size={14} className="spin" /> Scanning…</> : <><Search size={14} /> Scan for duplicates</>}
        </button>
        {data && data.clusters.length > 0 && (
          <button className="btn" onClick={applyAll} disabled={busy} data-testid="dedupe-calev-apply"
                  style={{ background: "#a32424", color: "#fff", borderColor: "#a32424" }}>
            <AlertTriangle size={14} /> {busy ? "Removing…" : `Remove ${data.total_duplicate_rows} duplicate(s)`}
          </button>
        )}
        {data && (
          <span className="t-mut" style={{ fontSize: 13 }}>
            <b style={{ color: "var(--text-1)" }}>{data.total_clusters}</b> cluster(s) · <b style={{ color: data.total_duplicate_rows ? "var(--red)" : "var(--text-1)" }}>{data.total_duplicate_rows}</b> duplicate event row(s)
          </span>
        )}
      </div>

      {data && data.clusters.length === 0 && (
        <div className="notice" style={{ background: "var(--card-2)" }}>
          <CheckCircle2 size={15} color="var(--green)" />
          <span>No duplicate calibration events found.</span>
        </div>
      )}

      {data?.clusters?.length > 0 && (
        <div className="tbl-wrap">
          <table>
            <thead><tr><th>Customer</th><th>Equipment</th><th>SN / P/N</th><th>Cal date</th><th>Kept (oldest)</th><th>Dups to delete</th></tr></thead>
            <tbody>
              {data.clusters.map((cl, i) => (
                <tr key={cl.master?._id || cl.master?.id || `${cl.equipment?.serial_number || ""}-${cl.key?.calibration_date || ""}-${i}`}>
                  <td className="t-strong">{cl.equipment?.customer_name || "—"}</td>
                  <td>{cl.equipment?.description || "—"}</td>
                  <td className="mono t-mut">{cl.equipment?.serial_number || "—"} · {cl.equipment?.part_number || "—"}</td>
                  <td className="mono">{cl.key.calibration_date}</td>
                  <td className="mono t-mut">{fmtDate(cl.master.created_at)}{cl.master.certificate_number ? ` · ${cl.master.certificate_number}` : ""}</td>
                  <td className="mono" style={{ color: "var(--red)", fontWeight: 700 }}>{cl.duplicates.length}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ============================================================
//  Customer dedupe — same company name (loose match) + lift contacts
// ============================================================
function CustomerDedupe() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyCluster, setBusyCluster] = useState(null);
  const [selected, setSelected] = useState({});
  const [bulkBusy, setBulkBusy] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillRes, setBackfillRes] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const [reconcileRes, setReconcileRes] = useState(null);
  const [fuzzy, setFuzzy] = useState(false);
  const [maxEdits, setMaxEdits] = useState(2);
  const [matchMode, setMatchMode] = useState("name"); // "name" | "email" | "phone"

  const scan = async () => {
    setLoading(true);
    setSelected({});
    try {
      const params = { limit: 500, mode: matchMode };
      if (matchMode === "name") {
        params.fuzzy = fuzzy ? "true" : "false";
        params.max_edits = maxEdits;
      }
      const r = await api.get("/dedupe/customers", { params });
      setData(r.data);
      if (r.data.total_clusters === 0) toast.success("No duplicate customers found.");
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  const toggleAll = () => {
    if (!data) return;
    const allSelected = data.clusters.length > 0 && data.clusters.every((c) => selected[c.master.id]);
    if (allSelected) setSelected({});
    else {
      const next = {};
      data.clusters.forEach((c) => { next[c.master.id] = true; });
      setSelected(next);
    }
  };
  const toggleOne = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));

  const selectedClusters = useMemo(
    () => (data?.clusters || []).filter((c) => selected[c.master.id]),
    [data, selected]
  );
  const selectedDupCount = useMemo(
    () => selectedClusters.reduce((acc, c) => acc + c.duplicates.length, 0),
    [selectedClusters]
  );

  const mergeBulk = async () => {
    if (!selectedClusters.length) return;
    if (!window.confirm(
      `Merge ${selectedClusters.length} customer cluster${selectedClusters.length === 1 ? "" : "s"} ` +
      `(${selectedDupCount} duplicate row${selectedDupCount === 1 ? "" : "s"}) into their masters?\n\n` +
      `For each duplicate row:\n` +
      `• All equipment, jobs, certificates, RFQs and items get re-pointed to the master\n` +
      `• The duplicate's contact (name + email + phone) is lifted into the master's contact list (deduped by email)\n` +
      `• The duplicate customer row is soft-deleted\n\n` +
      `This is fully audited. Cannot be undone.`
    )) return;
    setBulkBusy(true);
    try {
      const payload = {
        clusters: selectedClusters.map((c) => ({
          master_id: c.master.id,
          duplicate_ids: c.duplicates.map((d) => d.id),
        })),
      };
      const r = await api.post("/dedupe/customers/merge-bulk", payload);
      const m = r.data;
      toast.success(
        `${m.clusters_merged} cluster(s) merged · ${m.duplicates_merged} customer(s) collapsed · ` +
        `${m.contacts_lifted} new contact(s) added · ${m.equipment_moved} equipment moved` +
        (m.clusters_failed ? ` · ${m.clusters_failed} failed` : "")
      );
      if (m.errors?.length) console.warn("Customer merge errors:", m.errors);
      const mergedIds = new Set(selectedClusters.map((c) => c.master.id));
      setData((d) => ({
        ...d,
        clusters: d.clusters.filter((c) => !mergedIds.has(c.master.id)),
        total_clusters: Math.max(0, (d.total_clusters || 0) - m.clusters_merged),
        total_duplicate_rows: Math.max(0, (d.total_duplicate_rows || 0) - m.duplicates_merged),
      }));
      setSelected({});
    } catch (e) { toast.error(apiErr(e)); } finally { setBulkBusy(false); }
  };

  const merge = async (cluster) => {
    const dupIds = cluster.duplicates.map((d) => d.id);
    if (!window.confirm(
      `Merge ${dupIds.length} duplicate customer${dupIds.length === 1 ? "" : "s"} into the master?\n\n` +
      `Master: ${cluster.master.customer_name} (${cluster.master.customer_number || "no ID"})\n\n` +
      `Each duplicate's contact will be added to the master's contact list (skipped if email already exists). ` +
      `All linked equipment, jobs and certificates will be re-pointed to the master. Cannot be undone (but audited).`
    )) return;
    const key = cluster.master.id;
    setBusyCluster(key);
    try {
      const r = await api.post("/dedupe/customers/merge", {
        master_id: cluster.master.id, duplicate_ids: dupIds,
      });
      toast.success(
        `${r.data.duplicates_merged} merged · ${r.data.contacts_lifted} contact(s) added · ` +
        `${r.data.equipment_moved} equipment + ${r.data.jobs_moved} jobs moved`
      );
      setData((d) => ({
        ...d,
        clusters: d.clusters.filter((c) => c.master.id !== key),
        total_clusters: Math.max(0, (d.total_clusters || 1) - 1),
        total_duplicate_rows: Math.max(0, (d.total_duplicate_rows || 0) - dupIds.length),
      }));
      setSelected((s) => { const n = { ...s }; delete n[key]; return n; });
    } catch (e) { toast.error(apiErr(e)); } finally { setBusyCluster(null); }
  };

  const backfillIds = async () => {
    if (!window.confirm(
      "Assign customer IDs to all customers that don't have one?\n\n" +
      "Format: XXX-NNN (e.g. BAR-001) using the first 3 letters of the customer name. " +
      "Customers that already have a number are not touched. Numbers are also pushed onto their " +
      "linked jobs, items, equipment and RFQs.\n\n" +
      "Safe to run repeatedly."
    )) return;
    setBackfilling(true);
    setBackfillRes(null);
    try {
      const r = await api.post("/customers/backfill-numbers");
      setBackfillRes(r.data);
      if (r.data.assigned === 0) {
        toast.success("All customers already have IDs — nothing to assign.");
      } else {
        toast.success(`Assigned ${r.data.assigned} new customer ID(s).`);
      }
    } catch (e) { toast.error(apiErr(e)); } finally { setBackfilling(false); }
  };

  const reconcileOrphans = async () => {
    if (!window.confirm(
      "Reconcile orphan rows still pointing at merged customers?\n\n" +
      "Walks every soft-deleted (merged) customer and re-points any equipment, " +
      "jobs, certificates, RFQs and calibration events still attached to it onto " +
      "the resolved master. Fixes cases where older merges left orphan rows " +
      "(symptom: Receiving can't find a serial number that exists in the register).\n\n" +
      "Idempotent and audited."
    )) return;
    setReconciling(true);
    setReconcileRes(null);
    try {
      const r = await api.post("/dedupe/customers/reconcile-orphans");
      setReconcileRes(r.data);
      const moved = r.data.total_rows_moved || 0;
      if (moved === 0) toast.success("Already clean — no orphan rows to reconcile.");
      else toast.success(`Reconciled ${moved} orphan row(s) across ${r.data.customers_scanned} merged customer(s).`);
    } catch (e) { toast.error(apiErr(e)); } finally { setReconciling(false); }
  };

  const allSelected = data?.clusters?.length > 0 && data.clusters.every((c) => selected[c.master.id]);

  return (
    <div className="card pad" style={{ marginTop: 16 }}>
      <div className="sec-h">
        <h3>Customer duplicates — {matchMode === "name" ? (fuzzy ? `fuzzy name (≤${maxEdits} edits)` : "exact name") : matchMode === "email" ? "match by email + city" : "match by phone + contact"}</h3>
        <Building2 size={18} color="var(--gold-deep)" />
      </div>
      <p style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 14 }}>
        {matchMode === "email"
          ? <>Groups customers whose <b>primary email + city</b> are identical. Catches the "different name, same contact" pattern (e.g. <span className="mono">1CT (PTY) Ltd</span> + <span className="mono">1CT - Caterpillar (CAT) Plant and Field Services (Pty) Ltd</span> both with the same email). Verify each cluster — same-email coincidences are rare but possible.</>
          : matchMode === "phone"
          ? <>Groups customers whose <b>phone + contact-person name</b> are identical. Useful when the same person operates under multiple trading names. Review each cluster before merging.</>
          : fuzzy
          ? <>Groups customers whose normalised company names are within <b>{maxEdits} character edits</b> of each other (catches typos like "Equipme<b>tn</b>" vs "Equipme<b>nt</b>"). Be careful — at higher edit thresholds unrelated companies may cluster together.</>
          : <>Groups customer rows where the normalised company name is identical (case-insensitive, punctuation-stripped).</>}
        {" "}On merge: every duplicate's <b>contact (name + email + phone)</b> is lifted into the master's contact list (deduped by email), all linked equipment / jobs / certificates are re-pointed onto the master, then the duplicate row is soft-deleted.
      </p>

      {/* Backfill customer IDs sub-section */}
      <div className="card pad" style={{ background: "var(--card-2)", marginBottom: 16, borderLeft: "3px solid var(--gold-deep)" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 14, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 300 }}>
            <h4 style={{ margin: "0 0 4px", display: "flex", alignItems: "center", gap: 8 }}>
              <Hash size={15} color="var(--gold-deep)" /> Backdate Customer IDs
            </h4>
            <p style={{ fontSize: 12.5, color: "var(--text-2)", margin: 0 }}>
              Assigns <span className="mono">XXX-NNN</span> customer IDs to every customer that doesn't have one yet. Pre-existing IDs are kept. Numbers are denormalised onto linked jobs / items / equipment / RFQs.
            </p>
            {backfillRes && (
              <div style={{ marginTop: 10, fontSize: 12.5 }}>
                <span className="badge b-green"><span className="bd" />{backfillRes.assigned} ID(s) assigned</span>
                {Object.keys(backfillRes.by_prefix || {}).length > 0 && (
                  <span className="t-mut mono" style={{ marginLeft: 10, fontSize: 11.5 }}>
                    Per prefix: {Object.entries(backfillRes.by_prefix).map(([p, n]) => `${p}=${n}`).join(", ")}
                  </span>
                )}
              </div>
            )}
          </div>
          <button className="btn gold" onClick={backfillIds} disabled={backfilling} data-testid="cust-backfill-ids-btn">
            {backfilling ? <><RefreshCw size={14} className="spin" /> Assigning…</> : <><Hash size={14} /> Backfill missing IDs</>}
          </button>
        </div>
      </div>

      {/* Reconcile orphan equipment / job rows sub-section */}
      <div className="card pad" style={{ background: "var(--card-2)", marginBottom: 16, borderLeft: "3px solid var(--gold-deep)" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 14, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 300 }}>
            <h4 style={{ margin: "0 0 4px", display: "flex", alignItems: "center", gap: 8 }}>
              <Link2 size={15} color="var(--gold-deep)" /> Reconcile orphan rows
            </h4>
            <p style={{ fontSize: 12.5, color: "var(--text-2)", margin: 0 }}>
              Re-points any <b>equipment, jobs, certificates, RFQs and calibration events</b> still attached to a soft-deleted (merged) customer onto the resolved master. Fixes the symptom where Receiving can't find a serial that exists in the Equipment Register. Idempotent.
            </p>
            {reconcileRes && (
              <div style={{ marginTop: 10, fontSize: 12.5 }}>
                <span className="badge b-green"><span className="bd" />{reconcileRes.total_rows_moved || 0} row(s) moved</span>
                {Object.keys(reconcileRes.moves_by_collection || {}).length > 0 && (
                  <span className="t-mut mono" style={{ marginLeft: 10, fontSize: 11.5 }}>
                    {Object.entries(reconcileRes.moves_by_collection).map(([c, n]) => `${c}=${n}`).join(" · ")}
                  </span>
                )}
              </div>
            )}
          </div>
          <button className="btn gold" onClick={reconcileOrphans} disabled={reconciling} data-testid="cust-reconcile-orphans-btn">
            {reconciling ? <><RefreshCw size={14} className="spin" /> Reconciling…</> : <><Link2 size={14} /> Reconcile orphan rows</>}
          </button>
        </div>
      </div>

      {/* Re-link equipment by customer NAME sub-section (rescue tool) */}
      <RelinkByNameCard />

      {/* Find unreachable equipment (customer_id resolves to no customer at all) */}
      <UnreachableEquipmentCard />

      {/* Manual find + merge picker (escape hatch when auto-scan can't see something) */}
      <ManualMergeCard />

      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
        <div className="ftabs" style={{ marginRight: 8 }}>
          <button className={`ftab ${matchMode === "name" && !fuzzy ? "active" : ""}`}
                  onClick={() => { setMatchMode("name"); setFuzzy(false); setData(null); setSelected({}); }}
                  data-testid="dedupe-cust-mode-exact">
            Exact name
          </button>
          <button className={`ftab ${matchMode === "name" && fuzzy ? "active" : ""}`}
                  onClick={() => { setMatchMode("name"); setFuzzy(true); setData(null); setSelected({}); }}
                  data-testid="dedupe-cust-mode-fuzzy">
            Fuzzy name
          </button>
          <button className={`ftab ${matchMode === "email" ? "active" : ""}`}
                  onClick={() => { setMatchMode("email"); setFuzzy(false); setData(null); setSelected({}); }}
                  data-testid="dedupe-cust-mode-email">
            Email + city
          </button>
          <button className={`ftab ${matchMode === "phone" ? "active" : ""}`}
                  onClick={() => { setMatchMode("phone"); setFuzzy(false); setData(null); setSelected({}); }}
                  data-testid="dedupe-cust-mode-phone">
            Phone + contact
          </button>
        </div>
        {matchMode === "name" && fuzzy && (
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "var(--text-2)" }}>
            Max edits:
            <select value={maxEdits} onChange={(e) => { setMaxEdits(Number(e.target.value)); setData(null); }}
                    data-testid="dedupe-cust-max-edits"
                    style={{ padding: "4px 8px", border: "1px solid var(--line-2)", borderRadius: 6, fontFamily: "var(--mono)", fontSize: 12 }}>
              <option value={1}>1 (tight)</option>
              <option value={2}>2 (recommended)</option>
              <option value={3}>3</option>
              <option value={4}>4 (loose)</option>
            </select>
          </label>
        )}
        <button className="btn gold" onClick={scan} disabled={loading} data-testid="dedupe-cust-scan">
          {loading ? <><RefreshCw size={14} className="spin" /> Scanning…</> : <><Search size={14} /> Scan for duplicates</>}
        </button>
        {data && data.clusters.length > 0 && (
          <>
            <button className="btn ghost sm" onClick={toggleAll} data-testid="dedupe-cust-toggle-all">
              {allSelected ? "Deselect all" : `Select all (${data.clusters.length})`}
            </button>
            <button className="btn gold sm" onClick={mergeBulk}
                    disabled={bulkBusy || selectedClusters.length === 0}
                    data-testid="dedupe-cust-merge-selected">
              <GitMerge size={13} /> {bulkBusy
                ? `Merging ${selectedClusters.length}…`
                : `Merge ${selectedClusters.length} selected → ${selectedDupCount} dup${selectedDupCount === 1 ? "" : "s"}`}
            </button>
          </>
        )}
        {data && (
          <span className="t-mut" style={{ fontSize: 13, marginLeft: "auto" }}>
            <b style={{ color: "var(--text-1)" }}>{data.total_clusters}</b> cluster(s) · <b style={{ color: data.total_duplicate_rows ? "var(--red)" : "var(--text-1)" }}>{data.total_duplicate_rows}</b> duplicate row(s)
          </span>
        )}
      </div>

      {data && data.clusters.length === 0 && (
        <div className="notice" style={{ background: "var(--card-2)" }}>
          <CheckCircle2 size={15} color="var(--green)" />
          <span>No duplicate customers found.</span>
        </div>
      )}

      {data?.clusters?.map((cl) => {
        const isSelected = !!selected[cl.master.id];
        return (
        <div key={cl.master.id} className="card pad"
             style={{ marginBottom: 12, background: "var(--card-2)",
                      borderLeft: `3px solid ${isSelected ? "var(--gold-deep)" : "var(--line)"}` }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", flex: 1 }}>
              <input type="checkbox" checked={isSelected}
                     onChange={() => toggleOne(cl.master.id)}
                     data-testid={`dedupe-cust-check-${cl.master.id}`}
                     style={{ width: 16, height: 16, cursor: "pointer" }} />
              <span>
                <span className="t-strong">{cl.master.customer_name}</span>
                <span className="mono t-mut" style={{ marginLeft: 8, fontSize: 12 }}>{cl.master.customer_number || "no ID"}</span>
                <span className="t-mut" style={{ marginLeft: 8, fontSize: 12 }}>· {cl.master.city || cl.master.province || "—"}</span>
              </span>
            </label>
            <button className="btn sm gold" onClick={() => merge(cl)}
                    disabled={busyCluster === cl.master.id || bulkBusy}
                    data-testid={`dedupe-cust-merge-${cl.master.id}`}>
              <GitMerge size={12} /> {busyCluster === cl.master.id ? "Merging…" : `Merge ${cl.duplicates.length} into master`}
            </button>
          </div>

          <div className="tbl-wrap">
            <table>
              <thead><tr><th style={{ width: 60 }}>Role</th><th>ID</th><th>Contact name</th><th>Email</th><th>Phone</th><th>City</th><th>Equipment</th><th>Contacts</th><th>Updated</th></tr></thead>
              <tbody>
                <tr style={{ background: "rgba(212,175,55,.08)" }}>
                  <td><span className="badge b-gold"><span className="bd" />Master</span></td>
                  <td className="mono">{cl.master.customer_number || "—"}</td>
                  <td className="t-strong">{cl.master.contact_person || "—"}</td>
                  <td className="mono">{cl.master.email || "—"}</td>
                  <td className="mono">{cl.master.phone || "—"}</td>
                  <td>{cl.master.city || "—"}</td>
                  <td className="mono t-strong">{(cl.master.equipment_count ?? 0).toLocaleString("en-ZA")}</td>
                  <td className="mono">{cl.master.portal_contacts_count ?? 0}</td>
                  <td className="mono t-mut">{fmtDate(cl.master.updated_at)}</td>
                </tr>
                {cl.duplicates.map((d) => (
                  <tr key={d.id}>
                    <td><span className="badge b-red"><span className="bd" />Dup</span></td>
                    <td className="mono">{d.customer_number || "—"}</td>
                    <td>{d.contact_person || "—"}</td>
                    <td className="mono">{d.email || "—"}</td>
                    <td className="mono">{d.phone || "—"}</td>
                    <td>{d.city || "—"}</td>
                    <td className="mono">{(d.equipment_count ?? 0).toLocaleString("en-ZA")}</td>
                    <td className="mono">{d.portal_contacts_count ?? 0}</td>
                    <td className="mono t-mut">{fmtDate(d.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );})}
    </div>
  );
}


// ============================================================
//  Job / DN dedupe — merge split jobs sharing the same delivery note
// ============================================================
function JobRow({ job, isMaster, checked, onSetMaster, onToggle, disabled }) {
  return (
    <tr style={{ background: isMaster ? "rgba(212,175,55,.08)" : undefined }}>
      <td>
        <button type="button" onClick={() => onSetMaster(job.id)} disabled={disabled}
                title="Make this job the master (survivor)"
                data-testid={`job-dedupe-star-${job.id}`}
                style={{ background: "none", border: "none", cursor: disabled ? "not-allowed" : "pointer",
                         color: isMaster ? "var(--gold-deep)" : "var(--text-3)", fontSize: 17 }}>
          {isMaster ? "★" : "☆"}
        </button>
      </td>
      <td>
        {!isMaster && (
          <input type="checkbox" checked={checked} onChange={() => onToggle(job.id)} disabled={disabled}
                 data-testid={`job-dedupe-check-${job.id}`} />
        )}
      </td>
      <td>{isMaster ? <span className="badge b-gold"><span className="bd" />Master</span> : <span className="badge b-red"><span className="bd" />Merge into master</span>}</td>
      <td className="mono">{job.item_count}</td>
      <td className="t-strong">{job.customer_name || "—"}</td>
      <td className="mono t-mut" style={{ fontSize: 11 }}>{job.customer_number || "—"}</td>
      <td className="mono t-mut" style={{ fontSize: 11 }}>{job.legacy_source_table || "—"}</td>
      <td className="mono t-mut">{fmtDate(job.created_at)}</td>
      <td>{job.completed ? "yes" : "—"}</td>
    </tr>
  );
}

function JobDedupeCluster({ cluster, onMerged }) {
  const allJobs = [cluster.master, ...cluster.duplicates];
  const [masterId, setMasterId] = useState(cluster.master.id);
  const [checked, setChecked] = useState(() => {
    const c = {};
    cluster.duplicates.forEach((d) => { c[d.id] = true; });
    return c;
  });
  const [busy, setBusy] = useState(false);

  const setMaster = (id) => {
    setMasterId(id);
    setChecked((s) => {
      const n = { ...s };
      delete n[id];
      allJobs.forEach((j) => { if (j.id !== id && !(j.id in n)) n[j.id] = true; });
      return n;
    });
  };
  const toggle = (id) => setChecked((s) => ({ ...s, [id]: !s[id] }));

  const dupIds = Object.keys(checked).filter((k) => checked[k]);
  const dupCount = dupIds.length;
  const master = allJobs.find((j) => j.id === masterId);
  const crossCust = allJobs.some((j) => j.customer_id !== master.customer_id);

  const merge = async () => {
    if (dupCount === 0) return toast.error("Select at least one job to merge into the master.");
    if (!window.confirm(
      `Merge ${dupCount} job${dupCount === 1 ? "" : "s"} on DN "${cluster.delivery_note_number}" into the master?\n\n` +
      `All their line items will move onto the master job` +
      (crossCust ? `, and any items belonging to a different customer will be re-stamped to the master's customer (${master.customer_name})` : "") +
      `. The merged job(s) will then be deleted. This is audited. Cannot be undone.`
    )) return;
    setBusy(true);
    try {
      const r = await api.post("/dedupe/jobs/merge", { master_id: masterId, duplicate_ids: dupIds });
      toast.success(
        `${r.data.jobs_merged} job(s) merged · ${r.data.items_moved} item(s) moved` +
        (r.data.items_customer_restamped ? ` · ${r.data.items_customer_restamped} re-stamped to master's customer` : "")
      );
      onMerged(masterId);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="card pad" style={{ marginBottom: 12, background: "var(--card-2)", borderLeft: `3px solid ${crossCust ? "var(--red)" : "var(--gold-deep)"}` }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 8 }}>
        <span>
          <span className="t-strong mono">DN {cluster.delivery_note_number}</span>
          <span className="t-mut" style={{ marginLeft: 10, fontSize: 12 }}>{allJobs.length} jobs</span>
          {crossCust && (
            <span className="badge b-red" style={{ marginLeft: 8 }}><span className="bd" />Spans multiple customers</span>
          )}
        </span>
        <button className="btn sm gold" onClick={merge} disabled={busy || dupCount === 0}
                data-testid={`job-dedupe-merge-${cluster.delivery_note_number}`}>
          <GitMerge size={12} /> {busy ? "Merging…" : `Merge ${dupCount} into master`}
        </button>
      </div>
      <div className="tbl-wrap">
        <table>
          <thead>
            <tr><th style={{ width: 30 }}>★</th><th style={{ width: 30 }}></th><th>Role</th><th>Items</th><th>Customer</th><th>ID</th><th>Legacy source</th><th>Created</th><th>Closed</th></tr>
          </thead>
          <tbody>
            {allJobs.map((j) => (
              <JobRow key={j.id} job={j} isMaster={j.id === masterId} checked={!!checked[j.id]}
                      onSetMaster={setMaster} onToggle={toggle} disabled={busy} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function JobItemRow({ item, isKeeper, checked, onSetKeeper, onToggle, disabled }) {
  return (
    <tr style={{ background: isKeeper ? "rgba(212,175,55,.08)" : undefined }}>
      <td>
        <button type="button" onClick={() => onSetKeeper(item.id)} disabled={disabled}
                title="Keep this line (survivor)" data-testid={`job-item-dedupe-star-${item.id}`}
                style={{ background: "none", border: "none", cursor: disabled ? "not-allowed" : "pointer",
                         color: isKeeper ? "var(--gold-deep)" : "var(--text-3)", fontSize: 17 }}>
          {isKeeper ? "★" : "☆"}
        </button>
      </td>
      <td>
        {!isKeeper && (
          <input type="checkbox" checked={checked} onChange={() => onToggle(item.id)} disabled={disabled}
                 data-testid={`job-item-dedupe-check-${item.id}`} />
        )}
      </td>
      <td>{isKeeper ? <span className="badge b-gold"><span className="bd" />Keep</span> : <span className="badge b-red"><span className="bd" />Duplicate</span>}</td>
      <td className="t-strong">{item.description || "—"}</td>
      <td className="mono t-mut" style={{ fontSize: 11 }}>{item.serial_number || item.issue_number || item.part_number || "—"}</td>
      <td>{item.status ? item.status.replace(/_/g, " ") : "—"}</td>
      <td className="mono t-mut" style={{ fontSize: 11 }}>{item.certificate_number || item.reserved_certificate_number || "—"}</td>
      <td className="mono t-mut">{fmtDate(item.created_at)}</td>
    </tr>
  );
}

function JobItemDedupeCluster({ cluster, onResolved }) {
  const allItems = [cluster.keeper, ...cluster.duplicates];
  const [keeperId, setKeeperId] = useState(cluster.keeper.id);
  const [checked, setChecked] = useState(() => {
    const c = {};
    cluster.duplicates.forEach((d) => { c[d.id] = true; });
    return c;
  });
  const [busy, setBusy] = useState(false);

  const setKeeper = (id) => {
    setKeeperId(id);
    setChecked((s) => {
      const n = { ...s };
      delete n[id];
      allItems.forEach((it) => { if (it.id !== id && !(it.id in n)) n[it.id] = true; });
      return n;
    });
  };
  const toggle = (id) => setChecked((s) => ({ ...s, [id]: !s[id] }));
  const dupIds = Object.keys(checked).filter((k) => checked[k]);

  const del = async () => {
    if (dupIds.length === 0) return toast.error("Select at least one duplicate line to remove.");
    if (!window.confirm(
      `Remove ${dupIds.length} duplicate line(s) of "${cluster.keeper.description}"?\n\n` +
      `Any reserved (not-yet-issued) certificate numbers on them are simply released — nothing else is renumbered. Cannot be undone.`
    )) return;
    setBusy(true);
    try {
      const r = await api.post("/dedupe/job-items/delete-duplicates", { job_id: cluster.job_id, duplicate_ids: dupIds });
      const blocked = r.data.blocked?.length || 0;
      toast.success(
        `${r.data.deleted.filter((d) => d.status === "deleted").length} duplicate line(s) removed` +
        (r.data.equipment_cleaned?.length ? ` · ${r.data.equipment_cleaned.length} orphaned equipment record(s) cleaned up` : "") +
        (blocked ? ` · ${blocked} skipped (already has real cert/invoice data)` : "")
      );
      onResolved();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="card pad" style={{ marginBottom: 10, background: "var(--card)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 8 }}>
        <span className="t-strong">{cluster.keeper.description || cluster.identity} <span className="t-mut" style={{ fontWeight: 400, fontSize: 12 }}>· {allItems.length} lines, same item</span></span>
        <button className="btn sm gold" onClick={del} disabled={busy || dupIds.length === 0}
                data-testid={`job-item-dedupe-delete-${cluster.keeper.id}`}>
          <Trash2 size={12} /> {busy ? "Removing…" : `Remove ${dupIds.length} duplicate line(s)`}
        </button>
      </div>
      <div className="tbl-wrap">
        <table>
          <thead>
            <tr><th style={{ width: 30 }}>★</th><th style={{ width: 30 }}></th><th>Role</th><th>Description</th><th>SN / Issue / Part</th><th>Status</th><th>Cert / Reserved</th><th>Created</th></tr>
          </thead>
          <tbody>
            {allItems.map((it) => (
              <JobItemRow key={it.id} item={it} isKeeper={it.id === keeperId} checked={!!checked[it.id]}
                          onSetKeeper={setKeeper} onToggle={toggle} disabled={busy} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function JobItemDedupePanel({ jobId, onClear }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/dedupe/job-items", { params: { job_id: jobId } });
      setData(r.data);
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [jobId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return <div className="card pad" style={{ marginBottom: 12 }} data-testid={`job-item-dedupe-loading-${jobId}`}>
      <RefreshCw size={13} className="spin" /> Checking this job for duplicate lines…
    </div>;
  }
  if (!data || data.total_clusters === 0) {
    return (
      <div className="notice" style={{ background: "var(--card-2)", marginBottom: 12 }} data-testid={`job-item-dedupe-clean-${jobId}`}>
        <CheckCircle2 size={15} color="var(--green)" />
        <span>Merged job checked — no duplicate lines found on it ({data?.total_items ?? 0} item(s)).</span>
      </div>
    );
  }
  const removeCluster = (identity) => setData((d) => {
    const remaining = d.clusters.filter((c) => c.identity !== identity);
    if (remaining.length === 0) { onClear(); }
    return { ...d, clusters: remaining, total_clusters: remaining.length };
  });

  return (
    <div className="card pad" style={{ marginBottom: 12, borderLeft: "3px solid var(--red)" }} data-testid={`job-item-dedupe-panel-${jobId}`}>
      <div className="t-strong" style={{ marginBottom: 8, display: "flex", alignItems: "center", gap: 8 }}>
        <AlertTriangle size={15} color="var(--red)" />
        This job also has {data.total_duplicate_items} duplicate line(s) across {data.total_clusters} item(s) — same bug, repeated lines instead of repeated jobs.
      </div>
      {data.clusters.map((c) => (
        <JobItemDedupeCluster key={c.identity} cluster={{ ...c, job_id: jobId }} onResolved={() => removeCluster(c.identity)} />
      ))}
    </div>
  );
}

function JobDedupeManualSearch({ onMergeMasterId }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);

  const search = async () => {
    if (!q.trim()) return toast.error("Type at least part of the delivery note number.");
    setBusy(true);
    try {
      const r = await api.get("/dedupe/jobs/find-by-dn", { params: { q: q.trim(), limit: 100 } });
      setItems(r.data.items || []);
      if ((r.data.items || []).length === 0) toast.warning("No jobs found on that DN.");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const cluster = items && items.length > 1 ? { delivery_note_number: q.trim(), master: items[0], duplicates: items.slice(1) } : null;

  return (
    <div className="card pad" style={{ background: "var(--card-2)", marginBottom: 16, borderLeft: "3px solid var(--gold)" }}>
      <h4 style={{ margin: "0 0 4px", display: "flex", alignItems: "center", gap: 8 }}>
        <Search size={15} color="var(--gold-deep)" /> Find & merge jobs manually by DN
      </h4>
      <p style={{ fontSize: 12.5, color: "var(--text-2)", margin: "0 0 10px" }}>
        Escape hatch for a client-reported DN — type any part of the delivery note number to see EVERY job on it (any customer), then star the master and merge the rest. Same tool as the auto-scan below, just triggered on demand.
      </p>
      <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <input className="input" placeholder="e.g. F-13-02.3-12" value={q} onChange={(e) => setQ(e.target.value)}
               onKeyDown={(e) => e.key === "Enter" && search()} data-testid="job-dedupe-search-input"
               style={{ flex: 1, minWidth: 240 }} />
        <button className="btn gold" onClick={search} disabled={busy} data-testid="job-dedupe-search-btn">
          {busy ? <><RefreshCw size={13} className="spin" /> Searching…</> : <><Search size={13} /> Search</>}
        </button>
      </div>
      {items && items.length === 1 && (
        <div className="notice" style={{ background: "var(--card)" }}>
          <CheckCircle2 size={14} color="var(--green)" />
          <span>Only 1 job found on that DN — nothing to merge.</span>
        </div>
      )}
      {items && items.length === 0 && (
        <div className="notice" style={{ background: "var(--card)" }}>
          <CheckCircle2 size={14} color="var(--text-3)" />
          <span>No jobs found on that DN.</span>
        </div>
      )}
      {cluster && <JobDedupeCluster cluster={cluster} onMerged={(masterId) => { setItems(null); onMergeMasterId(masterId); }} />}
    </div>
  );
}

function JobDedupe() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [crossCustomer, setCrossCustomer] = useState(false);
  const [itemCheckJobIds, setItemCheckJobIds] = useState([]);
  const [manualJobId, setManualJobId] = useState("");
  const [scanAllBusy, setScanAllBusy] = useState(false);
  const [scanAllResult, setScanAllResult] = useState(null);

  const scanAllItemDupes = async () => {
    setScanAllBusy(true);
    try {
      const r = await api.get("/dedupe/job-items/scan-all", { params: { limit: 50 } });
      setScanAllResult(r.data);
    } catch (e) { toast.error(apiErr(e)); } finally { setScanAllBusy(false); }
  };

  const scan = async () => {
    setLoading(true);
    try {
      const r = await api.get("/dedupe/jobs", { params: { limit: 300, cross_customer: crossCustomer } });
      setData(r.data);
      if (r.data.total_clusters === 0) toast.success("No split/duplicate jobs found on any DN.");
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  const removeCluster = (dn) => setData((d) => ({
    ...d,
    clusters: d.clusters.filter((c) => c.delivery_note_number !== dn),
    total_clusters: Math.max(0, (d.total_clusters || 1) - 1),
  }));

  const checkItemsOnJob = (jobId) => setItemCheckJobIds((ids) => (ids.includes(jobId) ? ids : [...ids, jobId]));
  const clearItemCheck = (jobId) => setItemCheckJobIds((ids) => ids.filter((id) => id !== jobId));

  return (
    <div className="card pad" style={{ marginTop: 16 }}>
      <div className="sec-h">
        <h3>Job / DN duplicates {crossCustomer ? "— any customer, same DN" : "— same DN + same customer"}</h3>
        <Link2 size={18} color="var(--gold-deep)" />
      </div>
      <p style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 10 }}>
        Finds delivery notes that were split into two or more separate job rows instead of one, AND lines that were
        repeated onto the SAME job (e.g. a resubmitted draft) — the two shapes of the DN-collision bug class seen
        this week. Merging a DN cluster automatically re-checks the surviving job for repeated lines too.
      </p>

      <JobDedupeManualSearch onMergeMasterId={checkItemsOnJob} />

      <div className="card pad" style={{ background: "var(--card-2)", marginBottom: 16, borderLeft: "3px solid var(--gold)" }}>
        <h4 style={{ margin: "0 0 4px", display: "flex", alignItems: "center", gap: 8 }}>
          <Hash size={15} color="var(--gold-deep)" /> Check one job for repeated/duplicate lines
        </h4>
        <p style={{ fontSize: 12.5, color: "var(--text-2)", margin: "0 0 10px" }}>
          Paste a job's ID (e.g. from the Job Flow Board or Dispatch's "Open in new tab" link) to check it directly
          for the same-line-repeated-N-times pattern, without needing a DN merge first.
        </p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input className="input" placeholder="Job ID" value={manualJobId} onChange={(e) => setManualJobId(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && manualJobId.trim() && checkItemsOnJob(manualJobId.trim())}
                 data-testid="job-item-dedupe-manual-input" style={{ flex: 1, minWidth: 240 }} />
          <button className="btn gold" onClick={() => manualJobId.trim() && checkItemsOnJob(manualJobId.trim())}
                  data-testid="job-item-dedupe-manual-btn">
            <Search size={13} /> Check job
          </button>
          <button className="btn dark" onClick={scanAllItemDupes} disabled={scanAllBusy} data-testid="job-item-dedupe-scan-all-btn">
            {scanAllBusy ? <><RefreshCw size={13} className="spin" /> Sweeping every job…</> : <><Search size={13} /> Or sweep ALL jobs for this pattern</>}
          </button>
        </div>
        {scanAllResult && (
          scanAllResult.total_jobs_with_duplicate_items === 0 ? (
            <div className="notice" style={{ background: "var(--card)", marginTop: 10 }}>
              <CheckCircle2 size={14} color="var(--green)" />
              <span>Swept {scanAllResult.candidate_jobs_checked} candidate job(s) system-wide — none besides what's already shown above.</span>
            </div>
          ) : (
            <div style={{ marginTop: 10 }}>
              <div className="t-mut" style={{ fontSize: 12, marginBottom: 6 }}>
                Found {scanAllResult.total_jobs_with_duplicate_items} more job(s) with repeated lines:
              </div>
              {scanAllResult.jobs.map((j) => (
                <div key={j.job_id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "6px 10px", background: "var(--card)", borderRadius: 6, marginBottom: 4 }}>
                  <span style={{ fontSize: 12.5 }}>
                    <span className="mono t-strong">DN {j.delivery_note_number || "—"}</span> · {j.customer_name} ·{" "}
                    <span style={{ color: "var(--red)" }}>{j.total_duplicate_items} duplicate line(s)</span> of {j.total_items} total
                  </span>
                  <button className="btn sm gold" onClick={() => checkItemsOnJob(j.job_id)} data-testid={`job-item-dedupe-scan-all-open-${j.job_id}`}>Review</button>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {itemCheckJobIds.map((jobId) => (
        <JobItemDedupePanel key={jobId} jobId={jobId} onClear={() => clearItemCheck(jobId)} />
      ))}

      <label className="notice" style={{ background: "var(--card-2)", marginBottom: 14, cursor: "pointer", display: "flex", alignItems: "flex-start", gap: 10 }}>
        <input type="checkbox" checked={crossCustomer}
               onChange={(e) => { setCrossCustomer(e.target.checked); setData(null); }}
               style={{ marginTop: 2 }} data-testid="job-dedupe-cross-customer-toggle" />
        <span>
          <b>Include cross-customer matches.</b> Also finds the same DN text shared by two <b>different</b> customer
          rows (e.g. a wrong-customer job shell, like the DN 5344 / DN 20260325-1 incidents) — not just splits within
          one customer. Merging a cross-customer group re-stamps the moved items to whichever job you star as master.
        </span>
      </label>

      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
        <button className="btn gold" onClick={scan} disabled={loading} data-testid="job-dedupe-scan">
          {loading ? <><RefreshCw size={14} className="spin" /> Scanning…</> : <><Search size={14} /> Scan for duplicate jobs</>}
        </button>
        {data && (
          <span className="t-mut" style={{ fontSize: 13 }}>
            <b style={{ color: "var(--text-1)" }}>{data.total_clusters}</b> DN(s) with duplicates ·{" "}
            <b style={{ color: data.total_duplicate_jobs ? "var(--red)" : "var(--text-1)" }}>{data.total_duplicate_jobs}</b> extra job(s)
            {data.total_clusters >= 300 && <span style={{ color: "var(--red)" }}> (showing first 300 — re-scan after merging some)</span>}
          </span>
        )}
      </div>

      {data && data.clusters.length === 0 && (
        <div className="notice" style={{ background: "var(--card-2)" }}>
          <CheckCircle2 size={15} color="var(--green)" />
          <span>No duplicate/split jobs found on this rule.</span>
        </div>
      )}

      {data?.clusters?.map((cl) => (
        <JobDedupeCluster key={cl.delivery_note_number} cluster={cl}
                           onMerged={(masterId) => { removeCluster(cl.delivery_note_number); checkItemsOnJob(masterId); }} />
      ))}
    </div>
  );
}



// ============================================================
//  Manual merge picker — escape hatch for when auto-scan misses things
// ============================================================
function ManualMergeCard() {
  const [q, setQ] = useState("");
  const [includeMerged, setIncludeMerged] = useState(true);
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState({});
  const [masterId, setMasterId] = useState(null);
  const [unmergeBusy, setUnmergeBusy] = useState(false);

  const search = async () => {
    if (!q.trim()) return toast.error("Type at least part of the customer name.");
    setBusy(true);
    setSelected({});
    setMasterId(null);
    try {
      const r = await api.get("/dedupe/customers/find-by-name", {
        params: { q: q.trim(), include_merged: includeMerged ? "true" : "false", limit: 100 },
      });
      setItems(r.data.items || []);
      if ((r.data.items || []).length === 0) toast.warning("No customers found by that name.");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const dupIds = selectedIds.filter((id) => id !== masterId);

  const doMerge = async () => {
    if (!masterId) return toast.error("Pick a master row first (click the star).");
    if (dupIds.length === 0) return toast.error("Select at least one duplicate to merge.");
    if (!window.confirm(
      `Merge ${dupIds.length} row(s) into the master?\n\n` +
      `All linked equipment / jobs / certificates from each duplicate will be re-pointed onto the master. ` +
      `Duplicate contacts (deduped by email) will be lifted onto the master's contact list. ` +
      `Duplicate customers will be soft-deleted. Audited.`
    )) return;
    setBusy(true);
    try {
      const r = await api.post("/dedupe/customers/merge", {
        master_id: masterId, duplicate_ids: dupIds,
      });
      toast.success(
        `${r.data.duplicates_merged} merged · ${r.data.contacts_lifted} new contact(s) · ` +
        `${r.data.equipment_moved} equipment + ${r.data.jobs_moved} jobs moved`
      );
      await search(); // re-fetch to refresh state
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const doUnmerge = async (id) => {
    if (!window.confirm(
      `Un-merge this row? It will become an ACTIVE customer again, but its previously-moved equipment / jobs / certificates STAY on the master. ` +
      `Only use this if you mistakenly merged the wrong row.`
    )) return;
    setUnmergeBusy(true);
    try {
      await api.post("/dedupe/customers/unmerge", {
        master_id: id, duplicate_ids: [id],
      });
      toast.success("Un-merged. Equipment was NOT moved back — you'll need to do that manually.");
      await search();
    } catch (e) { toast.error(apiErr(e)); } finally { setUnmergeBusy(false); }
  };

  return (
    <div className="card pad" style={{ background: "var(--card-2)", marginBottom: 16, borderLeft: "3px solid var(--gold)" }}>
      <h4 style={{ margin: "0 0 4px", display: "flex", alignItems: "center", gap: 8 }}>
        <Search size={15} color="var(--gold-deep)" /> Find & merge customers manually
      </h4>
      <p style={{ fontSize: 12.5, color: "var(--text-2)", margin: "0 0 10px" }}>
        Escape hatch when the auto-scan can't see obvious duplicates. Type part of the name → see EVERY matching row (including soft-merged ones) → tick the duplicates → pick a master (star) → merge. Also lets you <b>un-merge</b> rows you accidentally collapsed earlier.
      </p>
      <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <input className="input" placeholder="e.g. Automotive Equipment"
                value={q} onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && search()}
                data-testid="manual-merge-search-input"
                style={{ flex: 1, minWidth: 240 }} />
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "var(--text-2)" }}>
          <input type="checkbox" checked={includeMerged}
                  onChange={(e) => setIncludeMerged(e.target.checked)}
                  data-testid="manual-merge-include-merged" />
          Show soft-merged rows
        </label>
        <button className="btn gold" onClick={search} disabled={busy} data-testid="manual-merge-search-btn">
          {busy ? <><RefreshCw size={13} className="spin" /> Searching…</> : <><Search size={13} /> Search</>}
        </button>
      </div>

      {items && items.length > 0 && (
        <>
          <div style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span className="t-mut" style={{ fontSize: 12.5 }}>
              <b>{items.length}</b> row(s) — pick a <span style={{ color: "var(--gold-deep)" }}>★ master</span> and tick the duplicates to merge.
            </span>
            {masterId && dupIds.length > 0 && (
              <button className="btn gold sm" onClick={doMerge} disabled={busy}
                       data-testid="manual-merge-apply">
                <GitMerge size={13} /> Merge {dupIds.length} into master
              </button>
            )}
          </div>
          <div className="tbl-wrap" style={{ maxHeight: 460, overflowY: "auto" }}>
            <table>
              <thead>
                <tr>
                  <th style={{ width: 32 }}>★</th>
                  <th style={{ width: 32 }}></th>
                  <th>ID</th>
                  <th>Customer</th>
                  <th>Contact</th>
                  <th>Email</th>
                  <th>City</th>
                  <th>Status</th>
                  <th style={{ textAlign: "right" }}>Equip</th>
                  <th style={{ textAlign: "right" }}>Contacts</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => {
                  const isMerged = !!r.merged_into || r.active === false;
                  return (
                  <tr key={r.id} style={{ background: r.id === masterId ? "rgba(212,175,55,.10)" : isMerged ? "rgba(0,0,0,.03)" : undefined }}>
                    <td>
                      <button onClick={() => setMasterId(r.id === masterId ? null : r.id)}
                              title={isMerged ? "Cannot star a soft-merged row" : "Set as master"}
                              disabled={isMerged}
                              data-testid={`manual-merge-star-${r.id}`}
                              style={{ background: "none", border: "none", cursor: isMerged ? "not-allowed" : "pointer", color: r.id === masterId ? "var(--gold-deep)" : "var(--text-3)", fontSize: 17 }}>
                        {r.id === masterId ? "★" : "☆"}
                      </button>
                    </td>
                    <td>
                      {!isMerged && r.id !== masterId && (
                        <input type="checkbox" checked={!!selected[r.id]}
                                onChange={() => toggle(r.id)}
                                data-testid={`manual-merge-check-${r.id}`} />
                      )}
                    </td>
                    <td className="mono" style={{ fontSize: 12 }}>{r.customer_number || "—"}</td>
                    <td>
                      <span className="t-strong">{r.customer_name}</span>
                      {r.name_raw_repr !== `'${r.customer_name}'` && (
                        <span className="t-mut mono" style={{ fontSize: 10.5, marginLeft: 6 }} title="Raw repr — check for invisible characters">
                          {r.name_raw_repr}
                        </span>
                      )}
                    </td>
                    <td>{r.contact_person || "—"}</td>
                    <td className="mono" style={{ fontSize: 12 }}>{r.email || "—"}</td>
                    <td>{r.city || "—"}</td>
                    <td>
                      {isMerged
                        ? <span className="badge b-red"><span className="bd" />Soft-merged</span>
                        : <span className="badge b-green"><span className="bd" />Active</span>}
                    </td>
                    <td className="mono" style={{ textAlign: "right" }}>{r.equipment_count}</td>
                    <td className="mono" style={{ textAlign: "right" }}>{r.portal_contacts_count}</td>
                    <td>
                      {isMerged && (
                        <button className="btn sm ghost" onClick={() => doUnmerge(r.id)}
                                disabled={unmergeBusy}
                                title="Restore this customer (equipment will not move back)"
                                data-testid={`manual-unmerge-${r.id}`}>
                          <RotateCcw size={11} /> Un-merge
                        </button>
                      )}
                    </td>
                  </tr>
                );})}
              </tbody>
            </table>
          </div>
        </>
      )}

      {items && items.length === 0 && (
        <div className="notice" style={{ background: "var(--card)" }}>
          <CheckCircle2 size={14} color="var(--text-3)" />
          <span>No customers found by that name.</span>
        </div>
      )}
    </div>
  );
}


// ============================================================
//  Re-link orphan equipment by customer NAME (rescue tool)
// ============================================================
function RelinkByNameCard() {
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState("idle"); // idle | dry | done
  const [result, setResult] = useState(null);

  const run = async (dry) => {
    setBusy(true);
    try {
      const r = await api.post(`/dedupe/equipment/relink-by-name?dry_run=${dry ? "true" : "false"}`);
      setResult(r.data);
      setStage(dry ? "dry" : "done");
      if (dry) {
        if (r.data.total_moved === 0) toast.success("Nothing to re-link — all equipment is correctly attached.");
        else toast.success(`Dry-run: ${r.data.total_moved} row(s) would be re-linked. Review then click Apply.`);
      } else {
        toast.success(`Re-linked ${r.data.total_moved} row(s) by customer name.`);
      }
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="card pad" style={{ background: "var(--card-2)", marginBottom: 16, borderLeft: "3px solid var(--red)" }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 14, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 300 }}>
          <h4 style={{ margin: "0 0 4px", display: "flex", alignItems: "center", gap: 8 }}>
            <AlertTriangle size={15} color="var(--red)" /> Re-link orphan equipment by customer name
          </h4>
          <p style={{ fontSize: 12.5, color: "var(--text-2)", margin: 0 }}>
            For any equipment / job / certificate whose <span className="mono">customer_id</span> points at a customer that no longer exists (or is soft-deleted with no resolved master), looks up an <b>active</b> customer with the same <span className="mono">customer_name</span> and re-points the row. Fixes the symptom of "0 equipment" on a customer that's clearly in the global Equipment Register. Always click <b>Dry-run</b> first to preview.
          </p>
          {result && (
            <div style={{ marginTop: 10, fontSize: 12.5 }}>
              <span className={`badge ${stage === "done" ? "b-green" : "b-amber"}`}>
                <span className="bd" />
                {stage === "done" ? "Applied" : "Dry-run"} · {result.total_moved} row(s)
              </span>
              {Object.keys(result.rows_moved || {}).length > 0 && (
                <span className="t-mut mono" style={{ marginLeft: 10, fontSize: 11.5 }}>
                  {Object.entries(result.rows_moved).map(([c, n]) => `${c}=${n}`).join(" · ")}
                </span>
              )}
              {(result.ambiguous_name_clusters || 0) > 0 && (
                <div className="t-mut" style={{ fontSize: 11.5, marginTop: 4 }}>
                  ⚠ {result.ambiguous_name_clusters} active customer name(s) are duplicated — those rows were SKIPPED to avoid mis-pointing. Merge those duplicate customers first, then re-run.
                </div>
              )}
              {result.sample_moves?.length > 0 && stage === "dry" && (
                <details style={{ marginTop: 8 }}>
                  <summary style={{ cursor: "pointer", fontSize: 12, color: "var(--gold-deep)" }}>
                    Preview {Math.min(result.sample_moves.length, 25)} sample row(s)
                  </summary>
                  <div className="mono" style={{ fontSize: 11, marginTop: 6, maxHeight: 200, overflowY: "auto", background: "var(--card)", padding: 8, borderRadius: 6 }}>
                    {result.sample_moves.map((m, i) => (
                      <div key={`${m.collection}-${m.old_customer_id}-${m.new_customer_id}-${i}`}>{m.collection}: {m.customer_name} ({m.old_customer_id.slice(0, 8)}… → {m.new_customer_id.slice(0, 8)}…)</div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <button className="btn ghost sm" onClick={() => run(true)} disabled={busy} data-testid="relink-dry-run-btn">
            {busy && stage === "idle" ? <><RefreshCw size={13} className="spin" /> Scanning…</> : <><Search size={13} /> Dry-run (preview)</>}
          </button>
          <button className="btn" style={{ background: "var(--red)", color: "#fff", borderColor: "var(--red)" }}
                  onClick={() => {
                    if (!window.confirm(
                      `Apply re-link for ${result?.total_moved || 0} row(s)?\n\n` +
                      "This re-points equipment/jobs/certificates onto active customers based on customer_name match. " +
                      "Audited and reversible only via manual edit."
                    )) return;
                    run(false);
                  }}
                  disabled={busy || !result || result.total_moved === 0 || stage === "done"}
                  data-testid="relink-apply-btn">
            <Link2 size={13} /> Apply re-link
          </button>
        </div>
      </div>
    </div>
  );
}


// ============================================================
//  Find unreachable equipment (lost rows manual reattach)
// ============================================================
function UnreachableEquipmentCard() {
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState({});
  const [picker, setPicker] = useState(false);
  const [pickQ, setPickQ] = useState("");
  const [pickResults, setPickResults] = useState(null);
  const [pickBusy, setPickBusy] = useState(false);
  const [reattachBusy, setReattachBusy] = useState(false);

  const scan = async () => {
    setBusy(true);
    setSelected({});
    try {
      const r = await api.get("/dedupe/equipment/unreachable", { params: { limit: 500 } });
      setData(r.data);
      if (r.data.total === 0) toast.success("No unreachable equipment — every row points at a real customer.");
      else toast.success(`Found ${r.data.total} unreachable equipment row(s).`);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const toggleOne = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const toggleAll = () => {
    if (!data) return;
    const all = data.items.length > 0 && data.items.every((it) => selected[it.id]);
    if (all) setSelected({});
    else {
      const next = {};
      data.items.forEach((it) => { next[it.id] = true; });
      setSelected(next);
    }
  };
  const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);

  const acceptSuggestion = async (item) => {
    if (!item.name_match) return;
    if (!window.confirm(
      `Re-attach this equipment to "${item.name_match.customer_name}"?\n\n` +
      `Linked certificates / calibration events / job items will follow.`
    )) return;
    await doReattach(item.name_match.id, item.name_match.customer_name, [item.id]);
  };

  const searchCustomers = async () => {
    if (!pickQ.trim()) return toast.error("Type at least part of the customer name.");
    setPickBusy(true);
    try {
      const r = await api.get("/dedupe/customers/find-by-name", {
        params: { q: pickQ.trim(), include_merged: "false", limit: 50 },
      });
      setPickResults(r.data.items || []);
      if ((r.data.items || []).length === 0) toast.warning("No active customers found by that name.");
    } catch (e) { toast.error(apiErr(e)); } finally { setPickBusy(false); }
  };

  const doReattach = async (customerId, customerName, ids) => {
    setReattachBusy(true);
    try {
      const r = await api.post("/dedupe/equipment/reattach", {
        customer_id: customerId, equipment_ids: ids,
      });
      const m = r.data;
      const childParts = Object.entries(m.child_moves || {}).map(([k, v]) => `${v} ${k}`).join(" · ");
      toast.success(
        `Re-attached ${m.equipment_moved} equipment to ${customerName}` +
        (childParts ? ` · ${childParts}` : "")
      );
      setPicker(false);
      setPickResults(null);
      setPickQ("");
      await scan(); // refresh
    } catch (e) { toast.error(apiErr(e)); } finally { setReattachBusy(false); }
  };

  const reattachSelected = async (customerId, customerName) => {
    if (!selectedIds.length) return toast.error("Select at least one equipment row first.");
    if (!window.confirm(
      `Re-attach ${selectedIds.length} equipment row(s) to "${customerName}"?\n\n` +
      `Linked certificates / calibration events / job items will follow. Audited.`
    )) return;
    await doReattach(customerId, customerName, selectedIds);
  };

  const allSelected = data?.items?.length > 0 && data.items.every((it) => selected[it.id]);

  return (
    <div className="card pad" style={{ background: "var(--card-2)", marginBottom: 16, borderLeft: "3px solid var(--red)" }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 14, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 300 }}>
          <h4 style={{ margin: "0 0 4px", display: "flex", alignItems: "center", gap: 8 }}>
            <Unlink size={15} color="var(--red)" /> Find unreachable equipment
          </h4>
          <p style={{ fontSize: 12.5, color: "var(--text-2)", margin: 0 }}>
            Scans the register for equipment whose <span className="mono">customer_id</span> resolves to <b>no customer at all</b> (neither active nor soft-merged). These are the residual "lost" rows that neither <i>Reconcile orphans</i> nor <i>Re-link by name</i> can fix — usually because the customer doc was hard-deleted or the id is stale. Re-attach each row to the correct customer manually. Audited.
          </p>
        </div>
        <button className="btn gold" onClick={scan} disabled={busy} data-testid="unreachable-scan-btn">
          {busy ? <><RefreshCw size={14} className="spin" /> Scanning…</> : <><Search size={14} /> Scan unreachable</>}
        </button>
      </div>

      {data && data.total === 0 && (
        <div className="notice" style={{ background: "var(--card)", marginTop: 12 }}>
          <CheckCircle2 size={14} color="var(--green)" />
          <span>Clean — every equipment row points at an existing customer.</span>
        </div>
      )}

      {data && data.items.length > 0 && (
        <>
          <div style={{ display: "flex", gap: 8, marginTop: 12, marginBottom: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span className="t-mut" style={{ fontSize: 12.5 }}>
              <b style={{ color: "var(--red)" }}>{data.total}</b> unreachable row(s){data.total >= (data.limit || 500) ? " (showing first " + data.limit + ")" : ""}
            </span>
            <button className="btn ghost sm" onClick={toggleAll} data-testid="unreachable-toggle-all">
              {allSelected ? "Deselect all" : `Select all (${data.items.length})`}
            </button>
            {selectedIds.length > 0 && (
              <button className="btn gold sm" onClick={() => setPicker(true)}
                      disabled={reattachBusy} data-testid="unreachable-reattach-selected-btn">
                <Link2 size={13} /> Re-attach {selectedIds.length} selected to a customer…
              </button>
            )}
          </div>
          <div className="tbl-wrap" style={{ maxHeight: 460, overflowY: "auto" }}>
            <table>
              <thead>
                <tr>
                  <th style={{ width: 32 }}></th>
                  <th>Equipment</th>
                  <th>Customer (broken)</th>
                  <th>Serial</th>
                  <th>Asset</th>
                  <th>Manufacturer</th>
                  <th>Last cal</th>
                  <th>Reason</th>
                  <th>Suggested match</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((it) => (
                  <tr key={it.id} style={{ background: selected[it.id] ? "rgba(212,175,55,.08)" : undefined }}>
                    <td>
                      <input type="checkbox" checked={!!selected[it.id]}
                              onChange={() => toggleOne(it.id)}
                              data-testid={`unreachable-check-${it.id}`} />
                    </td>
                    <td>
                      <span className="t-strong">{it.description || "—"}</span>
                      {it.part_number && (
                        <span className="mono t-mut" style={{ fontSize: 11, marginLeft: 6 }}>{it.part_number}</span>
                      )}
                    </td>
                    <td>
                      {it.customer_name || <span className="t-mut">— no name —</span>}
                      <div className="mono t-mut" style={{ fontSize: 10.5 }}>{it.customer_id || "(empty)"}</div>
                    </td>
                    <td className="mono" style={{ fontSize: 12 }}>{it.serial_number || "—"}</td>
                    <td className="mono" style={{ fontSize: 12 }}>{it.asset_number || "—"}</td>
                    <td>{it.manufacturer || "—"}</td>
                    <td className="mono t-mut" style={{ fontSize: 12 }}>{fmtDate(it.last_calibration_date)}</td>
                    <td>
                      {it.reason === "empty"
                        ? <span className="badge b-red"><span className="bd" />No customer_id</span>
                        : <span className="badge b-red"><span className="bd" />Stale id</span>}
                    </td>
                    <td>
                      {it.name_resolution === "unique" && it.name_match
                        ? (
                          <button className="btn sm ghost" onClick={() => acceptSuggestion(it)}
                                  disabled={reattachBusy}
                                  data-testid={`unreachable-accept-${it.id}`}
                                  title={`Re-attach to ${it.name_match.customer_name}`}>
                            <Link2 size={11} /> {it.name_match.customer_name?.slice(0, 24) || "Use match"}
                          </button>
                        )
                        : it.name_resolution === "ambiguous"
                        ? <span className="t-mut" style={{ fontSize: 11.5 }}>⚠ ambiguous name</span>
                        : it.name_resolution === "empty"
                        ? <span className="t-mut" style={{ fontSize: 11.5 }}>no name</span>
                        : <span className="t-mut" style={{ fontSize: 11.5 }}>no match</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {picker && (
        <div onClick={() => !reattachBusy && setPicker(false)}
             style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 200,
                      display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div onClick={(e) => e.stopPropagation()}
               className="card pad"
               style={{ width: "min(720px, 100%)", maxHeight: "82vh", overflowY: "auto", background: "var(--card)" }}>
            <h4 style={{ margin: "0 0 8px", display: "flex", alignItems: "center", gap: 8 }}>
              <Link2 size={15} color="var(--gold-deep)" />
              Re-attach {selectedIds.length} equipment row(s) to…
            </h4>
            <p style={{ fontSize: 12.5, color: "var(--text-2)", margin: "0 0 10px" }}>
              Search for the active customer that should own these equipment rows. Linked certificates / calibration events / job-items will also be re-pointed.
            </p>
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              <input className="input" placeholder="Type customer name…"
                      value={pickQ} onChange={(e) => setPickQ(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && searchCustomers()}
                      data-testid="unreachable-pick-input"
                      autoFocus
                      style={{ flex: 1 }} />
              <button className="btn gold" onClick={searchCustomers} disabled={pickBusy}
                       data-testid="unreachable-pick-search">
                {pickBusy ? <RefreshCw size={13} className="spin" /> : <Search size={13} />} Search
              </button>
            </div>
            {pickResults && pickResults.length > 0 && (
              <div className="tbl-wrap" style={{ maxHeight: 360 }}>
                <table>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Customer</th>
                      <th>City</th>
                      <th>Equipment</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pickResults.map((r) => (
                      <tr key={r.id}>
                        <td className="mono" style={{ fontSize: 12 }}>{r.customer_number || "—"}</td>
                        <td className="t-strong">{r.customer_name}</td>
                        <td>{r.city || "—"}</td>
                        <td className="mono">{r.equipment_count}</td>
                        <td>
                          <button className="btn sm gold" onClick={() => reattachSelected(r.id, r.customer_name)}
                                  disabled={reattachBusy}
                                  data-testid={`unreachable-pick-${r.id}`}>
                            <Link2 size={11} /> Re-attach here
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
              <button className="btn ghost sm" onClick={() => setPicker(false)} disabled={reattachBusy}
                       data-testid="unreachable-pick-close">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ============================================================
//  Cleanup Wizard — Scan → Merge equipment dups → Dedupe certs
// ============================================================
const STEPS = [
  { key: "scan_eq_strict", label: "Scan equipment dups (strict: SN + Part)" },
  { key: "merge_eq_strict", label: "Merge strict equipment clusters" },
  { key: "scan_eq_loose", label: "Scan equipment dups (loose: any 2 of Asset/SN/Part)" },
  { key: "merge_eq_loose", label: "Merge loose equipment clusters" },
  { key: "scan_cert", label: "Scan certificate duplicates" },
  { key: "dedupe_cert", label: "Dedupe certificates" },
  { key: "scan_calev", label: "Scan duplicate calibration dates" },
  { key: "dedupe_calev", label: "Dedupe calibration events" },
];

function CleanupWizard() {
  const [running, setRunning] = useState(false);
  const [log, setLog] = useState([]);
  const [done, setDone] = useState(false);
  const [stepStatus, setStepStatus] = useState({});

  const addLog = (line) => setLog((l) => [...l, { at: new Date().toLocaleTimeString(), msg: line }]);
  const setStep = (key, val) => setStepStatus((s) => ({ ...s, [key]: val }));

  const run = async () => {
    if (!window.confirm(
      "Run the full cleanup?\n\n" +
      "1. Scan + auto-merge STRICT equipment dups (same customer + serial + part #)\n" +
      "2. Scan + auto-merge LOOSE equipment dups (any 2 of Asset / Serial / Part match)\n" +
      "3. Scan + remove duplicate certificates (same equipment + cal date + CR # + part #)\n" +
      "4. Scan + remove duplicate calibration dates (same equipment + cal date)\n\n" +
      "Everything is audited and can be reviewed in the equipment audit log. Cannot be undone."
    )) return;

    setRunning(true);
    setDone(false);
    setLog([]);
    setStepStatus({});

    const runMergePass = async (mode, scanKey, mergeKey) => {
      setStep(scanKey, "running");
      addLog(`Scanning for ${mode} equipment duplicates…`);
      const eq = await api.get("/dedupe/equipment", { params: { limit: 500, mode } });
      setStep(scanKey, "done");
      addLog(`Found ${eq.data.total_clusters} cluster(s) · ${eq.data.total_duplicate_rows} duplicate row(s).`);
      if (!eq.data.clusters.length) {
        setStep(mergeKey, "skipped");
        addLog("Nothing to merge — skipped.");
        return;
      }
      setStep(mergeKey, "running");
      addLog(`Merging ${eq.data.clusters.length} ${mode} cluster(s)…`);
      const payload = {
        clusters: eq.data.clusters.map((c) => ({
          master_id: c.master.id,
          duplicate_ids: c.duplicates.map((d) => d.id),
        })),
      };
      const r = await api.post("/dedupe/equipment/merge-bulk", payload);
      setStep(mergeKey, "done");
      addLog(
        `Merged ${r.data.clusters_merged} ${mode} cluster(s) · ` +
        `${r.data.duplicates_deleted} dup(s) deleted · ${r.data.certs_moved} cert(s) + ${r.data.events_moved} event(s) + ${r.data.job_items_moved} job-line(s) moved.` +
        (r.data.clusters_failed ? ` ${r.data.clusters_failed} failed.` : "")
      );
    };

    try {
      await runMergePass("strict", "scan_eq_strict", "merge_eq_strict");
      await runMergePass("loose", "scan_eq_loose", "merge_eq_loose");

      setStep("scan_cert", "running");
      addLog("Scanning for duplicate certificates…");
      const cr = await api.get("/dedupe/certificates", { params: { limit: 500 } });
      setStep("scan_cert", "done");
      addLog(`Found ${cr.data.total_clusters} cluster(s) · ${cr.data.total_duplicate_rows} duplicate cert(s).`);

      if (cr.data.clusters.length === 0) {
        setStep("dedupe_cert", "skipped");
        addLog("No certificate duplicates to remove — skipped.");
      } else {
        setStep("dedupe_cert", "running");
        addLog(`Removing ${cr.data.total_duplicate_rows} duplicate cert(s)…`);
        const rc = await api.post("/dedupe/certificates/apply", {
          cluster_keys: cr.data.clusters.map((c) => c.key),
        });
        setStep("dedupe_cert", "done");
        addLog(`Removed ${rc.data.certs_deleted} duplicate cert(s) across ${rc.data.clusters_processed} cluster(s).`);
      }

      setStep("scan_calev", "running");
      addLog("Scanning for duplicate calibration dates…");
      const ce = await api.get("/dedupe/calibration-events", { params: { limit: 1000 } });
      setStep("scan_calev", "done");
      addLog(`Found ${ce.data.total_clusters} cluster(s) · ${ce.data.total_duplicate_rows} duplicate event(s).`);

      if (ce.data.clusters.length === 0) {
        setStep("dedupe_calev", "skipped");
        addLog("No duplicate calibration dates — skipped.");
      } else {
        setStep("dedupe_calev", "running");
        addLog(`Removing ${ce.data.total_duplicate_rows} duplicate event(s)…`);
        const rce = await api.post("/dedupe/calibration-events/apply", {
          cluster_keys: ce.data.clusters.map((c) => c.key),
        });
        setStep("dedupe_calev", "done");
        addLog(`Removed ${rce.data.events_deleted} duplicate event(s) across ${rce.data.clusters_processed} cluster(s).`);
      }

      setDone(true);
      toast.success("Cleanup complete — register normalised.");
    } catch (e) {
      addLog(`ERROR: ${apiErr(e)}`);
      toast.error(apiErr(e));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="card pad" style={{ marginTop: 16 }}>
      <div className="sec-h">
        <h3>Cleanup wizard — one-click normalise the register</h3>
        <Sparkles size={18} color="var(--gold-deep)" />
      </div>
      <p style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 14 }}>
        Chains all six reconciliation steps in one go: auto-merge strict equipment dups → auto-merge loose equipment dups (any 2 of Asset / Serial / Part) → remove duplicate certificates → remove duplicate calibration dates. Recommended after a fresh Caspio import or whenever the diff tool reports many "missing in PTE Connect" + drift rows.
      </p>

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 14 }}>
        <button className="btn gold" onClick={run} disabled={running} data-testid="cleanup-run">
          {running ? <><RefreshCw size={14} className="spin" /> Running…</> : <><Sparkles size={14} /> Run full cleanup</>}
        </button>
        {done && (
          <span className="badge b-green"><span className="bd" />Cleanup complete</span>
        )}
      </div>

      <div className="card pad" style={{ background: "var(--card-2)" }}>
        {STEPS.map((st) => {
          const s = stepStatus[st.key];
          const Icon = s === "done" ? CheckCircle2 : s === "running" ? RefreshCw : s === "skipped" ? ChevronRight : ChevronRight;
          const color = s === "done" ? "var(--green)" : s === "running" ? "var(--gold-deep)" : s === "skipped" ? "var(--text-2)" : "var(--text-2)";
          return (
            <div key={st.key} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", fontSize: 13 }}>
              <Icon size={15} className={s === "running" ? "spin" : ""} style={{ color }} />
              <span style={{ color: s ? "var(--text-1)" : "var(--text-2)" }}>{st.label}</span>
              {s && s !== "running" && <span className="t-mut" style={{ fontSize: 11.5, textTransform: "uppercase", letterSpacing: ".05em" }}>{s}</span>}
            </div>
          );
        })}
      </div>

      {log.length > 0 && (
        <div className="card pad" style={{ marginTop: 12, background: "#0d0d10", color: "#d9d7d0", fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: 12, maxHeight: 280, overflowY: "auto" }}>
          {log.map((l, i) => (
            <div key={`${l.at}-${i}`} style={{ padding: "2px 0" }}>
              <span style={{ color: "#7a7770", marginRight: 8 }}>{l.at}</span>
              <span>{l.msg}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}




// ============================================================
//  Caspio WIP / Subcontractor XLSX importer
// ============================================================
function CaspioWipImporterCard() {
  const [file, setFile] = useState(null);
  const [dryRun, setDryRun] = useState(true);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [showRows, setShowRows] = useState(false);

  const run = async () => {
    if (!file) { toast.error("Pick an .xlsx file first."); return; }
    setBusy(true); setReport(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("dry_run", dryRun ? "true" : "false");
      const r = await api.post("/import/caspio-wip-xlsx", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setReport(r.data);
      const c = r.data.counts || {};
      if (dryRun) {
        toast.success(`Dry-run complete — ${(c.would_create_or_update || 0)} rows ready to import, ${c.errors || 0} error(s).`);
      } else {
        toast.success(`Imported — ${c.created || 0} created, ${c.updated || 0} updated, ${c.errors || 0} error(s).`);
      }
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="card pad" style={{ marginTop: 16 }}>
      <div className="sec-h">
        <h3>Import Caspio WIP / Subcontractor XLSX</h3>
        <FileSpreadsheet size={18} color="var(--gold-deep)" />
      </div>
      <p style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 14 }}>
        Drop the Caspio <b>WIP / Sub</b> XLSX export and the tool will merge
        every row into the live system: <b>finds or creates the customer</b>,
        <b> matches existing equipment</b> (by Serial → Asset → Description, scoped to the customer),
        and either <b>creates a new job_item</b> or <b>updates the open one</b> on
        that equipment — never duplicating. The job_item status is derived from the outcome flags
        (Calibrated / Adjusted / Repaired / Scrapped / Returned / SubCon_Sent / SubCon_Received) and
        a row that's already further along in the workflow is <b>never rolled back</b>.
        Each sheet (WIP + Sub) is processed in one pass.
      </p>

      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
        <input type="file" accept=".xlsx,.xlsm"
          onChange={(e) => { setFile(e.target.files?.[0] || null); setReport(null); }}
          data-testid="caspio-wip-file" />
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)}
            data-testid="caspio-wip-dryrun" />
          Dry-run (preview only — don't write)
        </label>
        <button className={`btn ${dryRun ? "ghost" : "gold"}`} disabled={busy || !file}
          onClick={run} data-testid="caspio-wip-run">
          {busy ? <><RefreshCw size={14} className="spin" /> {dryRun ? "Previewing…" : "Importing…"}</>
                : <><Upload size={14} /> {dryRun ? "Preview impact" : "Run import"}</>}
        </button>
      </div>

      {report && (
        <div className="card pad" style={{ background: "var(--card-2)" }}>
          <div style={{ marginBottom: 10 }}>
            <span className="badge b-gold"><span className="bd" />{report.counts.rows_seen} row(s) seen</span>
            {dryRun ? (
              <span className="badge b-gold" style={{ marginLeft: 8 }}><span className="bd" />{report.counts.would_create_or_update || 0} ready</span>
            ) : (
              <>
                <span className="badge b-green" style={{ marginLeft: 8 }}><span className="bd" />{report.counts.created || 0} created</span>
                <span className="badge b-gold" style={{ marginLeft: 8 }}><span className="bd" />{report.counts.updated || 0} updated</span>
              </>
            )}
            {(report.counts.errors || 0) > 0 && (
              <span className="badge b-red" style={{ marginLeft: 8 }}>
                <AlertTriangle size={11} style={{ verticalAlign: "-2px", marginRight: 3 }} />
                {report.counts.errors} error(s)
              </span>
            )}
          </div>

          {report.sheets_processed?.length > 0 && (
            <div style={{ fontSize: 12.5, marginBottom: 10 }}>
              <b>Per-sheet:</b>{" "}
              {report.sheets_processed.map((s) => (
                <span key={s.name} className="mono t-mut" style={{ marginRight: 14 }}>
                  {s.name}: rows <b>{s.rows_seen}</b>
                  {!dryRun && <> · created <b style={{ color: "var(--gold-deep)" }}>{s.created || 0}</b> · updated <b style={{ color: "var(--gold-deep)" }}>{s.updated || 0}</b></>}
                  {dryRun && <> · ready <b style={{ color: "var(--gold-deep)" }}>{s.would_create_or_update || 0}</b></>}
                  {(s.errors || 0) > 0 && <> · <span style={{ color: "var(--red)" }}>errors <b>{s.errors}</b></span></>}
                </span>
              ))}
            </div>
          )}

          {report.errors?.length > 0 && (
            <details style={{ marginBottom: 10 }}>
              <summary style={{ cursor: "pointer", fontSize: 12.5, color: "var(--red)" }}>
                Show {report.errors.length} error(s)
              </summary>
              <div className="mono" style={{ fontSize: 11.5, marginTop: 6, maxHeight: 200, overflowY: "auto" }}>
                {report.errors.slice(0, 50).map((e, i) => (
                  <div key={`${e.sheet || ""}-${e.row || ""}-${i}`} style={{ marginBottom: 4 }}>
                    [{e.sheet || "—"} row {e.row}] {e.customer || "(no customer)"} {e.serial && `· SN ${e.serial}`} → <span style={{ color: "var(--red)" }}>{e.error}</span>
                  </div>
                ))}
              </div>
            </details>
          )}

          {report.results?.length > 0 && (
            <>
              <button className="btn sm ghost" onClick={() => setShowRows((v) => !v)}>
                {showRows ? "Hide" : "Show"} first {Math.min(report.results.length, 400)} row(s)
              </button>
              {showRows && (
                <div style={{ maxHeight: 320, overflowY: "auto", marginTop: 8 }}>
                  <table className="dt" style={{ width: "100%", fontSize: 11.5 }}>
                    <thead>
                      <tr>
                        <th>Sheet</th><th>Row</th><th>Action</th><th>Customer</th>
                        <th>DN</th><th>Status</th><th>Technician</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.results.map((r, i) => (
                        <tr key={`${r.sheet || ""}-${r.row || ""}-${i}`}>
                          <td className="mono">{r.sheet || "—"}</td>
                          <td className="mono">{r.row}</td>
                          <td>
                            <span className={"badge " + (
                              r.action === "created" ? "b-green" :
                              r.action === "updated" ? "b-gold" :
                              r.action === "skipped" ? "b-slate" : "b-gold"
                            )}><span className="bd" />{r.action.replace("would_create_or_update", "ready")}</span>
                          </td>
                          <td>{r.customer || "—"}</td>
                          <td className="mono">{r.delivery_note || "—"}</td>
                          <td className="mono">{(r.status || r.status_target || "").replace(/_/g, " ")}</td>
                          <td className="t-mut">{r.technician || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}


// ============================================================
//  Seed Active Lab from Caspio WIP equipment
// ============================================================
function SeedActiveLabCard() {
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [result, setResult] = useState(null);

  const doPreview = async () => {
    setPreviewing(true);
    setResult(null);
    try {
      const r = await api.get("/migrate/active-lab-preview");
      setPreview(r.data);
      if (r.data.total_to_seed === 0) toast.success("Active Lab is already in sync with Caspio WIP — nothing to seed.");
      else toast.success(`Preview: ${r.data.total_to_seed.toLocaleString("en-ZA")} equipment row(s) across ${r.data.distinct_jobs_to_create} job(s).`);
    } catch (e) { toast.error(apiErr(e)); } finally { setPreviewing(false); }
  };

  const doSeed = async () => {
    if (!window.confirm(
      `Seed ${(preview?.total_to_seed || 0).toLocaleString("en-ZA")} equipment row(s) across ` +
      `${preview?.distinct_jobs_to_create || 0} job(s) into Active Lab?\n\n` +
      `Criterion: active_in_lab == True (Caspio's authoritative WIP flag).\n` +
      `Grouping: one Job per (Customer + Delivery Note), multiple items per Job.\n` +
      `Equipment rows are marked as seeded so re-running is a no-op. Audited.`
    )) return;
    setSeeding(true);
    try {
      const r = await api.post("/migrate/seed-active-lab");
      setResult(r.data);
      setPreview(null);
      toast.success(
        `Seeded ${r.data.job_items_created} item(s) into ${r.data.jobs_created} new job(s) ` +
        `(reused ${r.data.jobs_reused} existing).`
      );
    } catch (e) { toast.error(apiErr(e)); } finally { setSeeding(false); }
  };

  const doUndo = async () => {
    if (!window.confirm(
      "Undo previous Caspio WIP seed?\n\n" +
      "This deletes every job + job_item created by the seed migration " +
      "(identified by legacy_source_table='caspio_wip_seed') and clears the " +
      "caspio_lab_seeded flag on equipment so a fresh seed can be run with " +
      "the corrected criterion (active_in_lab=True).\n\n" +
      "Manual entries (Receiving, etc.) are NOT touched. Audited."
    )) return;
    setUndoing(true);
    try {
      const r = await api.post("/migrate/seed-active-lab/undo");
      setResult(null);
      setPreview(null);
      toast.success(
        `Rolled back: ${r.data.job_items_deleted} item(s) + ${r.data.jobs_deleted} job(s) deleted, ` +
        `${r.data.equipment_flags_cleared} equipment flags cleared.`
      );
    } catch (e) { toast.error(apiErr(e)); } finally { setUndoing(false); }
  };

  return (
    <div className="card pad" style={{ marginTop: 16 }}>
      <div className="sec-h">
        <h3>Seed Active Lab from Caspio WIP equipment</h3>
        <Sparkles size={18} color="var(--gold-deep)" />
      </div>
      <p style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 14 }}>
        Caspio imports landed in the <span className="mono">equipment</span> collection but did NOT create matching <span className="mono">job_items</span>, so any equipment that was <b>active in the Caspio lab</b> at the time of cutover is invisible to Active Lab / My Worklist / Job Flow. This tool uses Caspio's authoritative <span className="mono">active_in_lab == True</span> flag (which corresponds to <span className="mono">-1</span> in Caspio's Yes/No fields) and creates one Job per (Customer + Delivery Note Number), with all equipment under that DN grouped beneath it. Idempotent: each equipment is flagged after seeding so a second run is a no-op.
      </p>

      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
        <button className="btn ghost" onClick={doPreview} disabled={previewing} data-testid="seed-active-lab-preview-btn">
          {previewing ? <><RefreshCw size={14} className="spin" /> Counting…</> : <><Search size={14} /> Preview impact</>}
        </button>
        {preview && preview.total_to_seed > 0 && (
          <button className="btn gold" onClick={doSeed} disabled={seeding} data-testid="seed-active-lab-apply-btn">
            {seeding ? <><RefreshCw size={14} className="spin" /> Seeding…</> : <><Sparkles size={14} /> Seed {preview.total_to_seed.toLocaleString("en-ZA")} items into {preview.distinct_jobs_to_create} job(s)</>}
          </button>
        )}
        <button className="btn ghost sm" onClick={doUndo} disabled={undoing} data-testid="seed-active-lab-undo-btn"
                style={{ marginLeft: "auto", color: "var(--red)" }}
                title="Roll back the previous seed (deletes all caspio_wip_seed rows + clears flags)">
          {undoing ? <><RefreshCw size={13} className="spin" /> Rolling back…</> : <><RotateCcw size={13} /> Undo previous seed</>}
        </button>
      </div>

      {preview && (
        <div className="card pad" style={{ background: "var(--card-2)", marginBottom: 14 }}>
          <div style={{ marginBottom: 10 }}>
            <span className="badge b-gold"><span className="bd" />{preview.total_to_seed.toLocaleString("en-ZA")} equipment row(s)</span>
            <span className="badge b-gold" style={{ marginLeft: 8 }}><span className="bd" />{preview.distinct_jobs_to_create} job(s)</span>
            <span className="t-mut" style={{ marginLeft: 10, fontSize: 12 }}>
              ({preview.already_linked_equipment_ids} equipment already linked to job_items)
            </span>
          </div>
          {Object.keys(preview.by_status || {}).length > 0 && (
            <div style={{ fontSize: 12.5, marginBottom: 8 }}>
              <b>Caspio status distribution (informational):</b>{" "}
              {Object.entries(preview.by_status).map(([k, v]) => (
                <span key={k} className="mono t-mut" style={{ marginRight: 12 }}>
                  {k}: <b style={{ color: "var(--gold-deep)" }}>{v}</b>
                </span>
              ))}
            </div>
          )}
          {preview.by_customer_top10?.length > 0 && (
            <details>
              <summary style={{ cursor: "pointer", fontSize: 12.5, color: "var(--gold-deep)" }}>
                Top 10 customers by WIP count
              </summary>
              <div className="mono" style={{ fontSize: 11.5, marginTop: 6 }}>
                {preview.by_customer_top10.map((c, i) => (
                  <div key={c.customer_number || c.customer_name || i}>
                    {c.customer_number || "—"} · {c.customer_name}: <b>{c.count}</b>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}

      {result && (
        <div className="card pad" style={{ background: "var(--card-2)" }}>
          <div className="badge b-green"><span className="bd" />Seed complete</div>
          <div className="mono" style={{ fontSize: 12, marginTop: 8 }}>
            Equipment scanned: <b>{result.equipment_scanned}</b><br />
            Job_items created: <b style={{ color: "var(--green)" }}>{result.job_items_created}</b><br />
            Jobs created: <b>{result.jobs_created}</b> (reused {result.jobs_reused} existing)<br />
            {result.skipped_no_customer > 0 && <>Skipped (no customer): <b>{result.skipped_no_customer}</b></>}
          </div>
        </div>
      )}
    </div>
  );
}


// ============================================================
//  Fix WIP-seed gaps — missing technician / cert / subcon status
//  on job_items created by the Caspio WIP seed
// ============================================================
function FixWipSeedGapsCard() {
  const [preview, setPreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  const doScan = async () => {
    setScanning(true);
    setResult(null);
    try {
      const r = await api.post("/migrate/fix-wip-seed-gaps", null, { params: { apply: false } });
      setPreview(r.data);
      const total = r.data.status_fixed + r.data.cert_filled + r.data.technician_assigned + r.data.send_to_subcontractor_fixed;
      if (total === 0) toast.success("No gaps found — every seeded WIP job is already correct.");
      else toast.success(`Found ${total} field(s) to fix across ${r.data.scanned} scanned item(s).`);
    } catch (e) { toast.error(apiErr(e)); } finally { setScanning(false); }
  };

  const doApply = async () => {
    if (!window.confirm(
      `Apply fixes to ${preview?.scanned || 0} scanned WIP job(s)?\n\n` +
      `Backfills: missing technician (best-match from historical certificates), missing CR/certificate ` +
      `number, wrong subcontractor status, and moves any newly-technician-assigned "Received" item into ` +
      `"Assigned". Only touches records nobody has manually worked on since seeding (empty status history). ` +
      `Fully audited and safe to re-run.`
    )) return;
    setApplying(true);
    try {
      const r = await api.post("/migrate/fix-wip-seed-gaps", null, { params: { apply: true } });
      setResult(r.data);
      setPreview(null);
      toast.success(
        `Fixed: ${r.data.technician_assigned} technician(s) · ${r.data.cert_filled} cert(s) · ` +
        `${r.data.status_fixed} status · ${r.data.send_to_subcontractor_fixed} subcon flag(s)`
      );
    } catch (e) { toast.error(apiErr(e)); } finally { setApplying(false); }
  };

  return (
    <div className="card pad" style={{ marginTop: 16 }}>
      <div className="sec-h">
        <h3>Fix WIP-seed gaps (technician / certificate / subcontractor status)</h3>
        <Sparkles size={18} color="var(--gold-deep)" />
      </div>
      <p style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 14 }}>
        The Caspio WIP seed above didn't originally carry across the <b>technician</b> (matched from historical
        certificates), the <b>CR/certificate number</b> (stored separately from equipment), or the correct{" "}
        <b>subcontractor status</b> for jobs still awaiting a PO. This backfills all three, and bumps any
        newly-assigned item out of "Received" into "Assigned". Only touches <span className="mono">caspio_wip_seed</span>{" "}
        job_items nobody has manually worked on yet. Idempotent — safe to re-run any time.
      </p>
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
        <button className="btn ghost" onClick={doScan} disabled={scanning} data-testid="fix-wip-gaps-scan-btn">
          {scanning ? <><RefreshCw size={14} className="spin" /> Scanning…</> : <><Search size={14} /> Preview gaps</>}
        </button>
        {preview && (preview.status_fixed + preview.cert_filled + preview.technician_assigned + preview.send_to_subcontractor_fixed > 0) && (
          <button className="btn gold" onClick={doApply} disabled={applying} data-testid="fix-wip-gaps-apply-btn">
            {applying ? <><RefreshCw size={14} className="spin" /> Applying…</> : <><Sparkles size={14} /> Apply fixes</>}
          </button>
        )}
      </div>

      {preview && (
        <div className="card pad" style={{ background: "var(--card-2)", marginBottom: 14 }}>
          <span className="badge b-gold" style={{ marginRight: 8 }}><span className="bd" />{preview.scanned} scanned</span>
          <span className="badge b-gold" style={{ marginRight: 8 }}><span className="bd" />{preview.technician_assigned} technician match(es)</span>
          <span className="badge b-gold" style={{ marginRight: 8 }}><span className="bd" />{preview.cert_filled} cert(s)</span>
          <span className="badge b-gold" style={{ marginRight: 8 }}><span className="bd" />{preview.status_fixed} status fix(es)</span>
          <span className="badge b-gold"><span className="bd" />{preview.send_to_subcontractor_fixed} subcon flag(s)</span>
        </div>
      )}

      {result && (
        <div className="card pad" style={{ background: "var(--card-2)" }}>
          <div className="badge b-green"><span className="bd" />Applied</div>
          <div className="mono" style={{ fontSize: 12, marginTop: 8 }}>
            Scanned: <b>{result.scanned}</b><br />
            Technician(s) matched: <b style={{ color: "var(--green)" }}>{result.technician_assigned}</b><br />
            Certificate(s) filled: <b style={{ color: "var(--green)" }}>{result.cert_filled}</b><br />
            Status fix(es): <b style={{ color: "var(--green)" }}>{result.status_fixed}</b><br />
            Subcontractor flag fix(es): <b style={{ color: "var(--green)" }}>{result.send_to_subcontractor_fixed}</b>
          </div>
        </div>
      )}
    </div>
  );
}


const downloadXlsx = async (endpoint, fallbackName) => {
  const res = await api.get(endpoint, { responseType: "blob", timeout: 120000 });
  let fname = fallbackName;
  const cd = res.headers["content-disposition"] || "";
  const m = /filename="?([^"]+)"?/.exec(cd);
  if (m) fname = m[1];
  const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = fname;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
};

// ============================================================
//  WIP-seed duplicates — same real item created more than once by a
//  repeat/duplicate CSV import (no-serial items can't be matched)
// ============================================================
function WipSeedDuplicatesCard() {
  const [preview, setPreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [result, setResult] = useState(null);

  const doExport = async () => {
    setExporting(true);
    try {
      await downloadXlsx("/migrate/wip-seed-duplicates/export.xlsx", "wip_seed_duplicates.xlsx");
      toast.success("Duplicate review exported.");
    } catch (e) { toast.error(apiErr(e)); } finally { setExporting(false); }
  };

  const doScan = async () => {
    setScanning(true);
    setResult(null);
    try {
      const r = await api.get("/migrate/wip-seed-duplicates");
      setPreview(r.data);
      if (r.data.total_duplicate_rows === 0) toast.success("No duplicates found.");
      else toast.success(`Found ${r.data.total_duplicate_rows} duplicate row(s) across ${r.data.total_clusters} item(s).`);
    } catch (e) { toast.error(apiErr(e)); } finally { setScanning(false); }
  };

  const doApply = async () => {
    if (!window.confirm(
      `Archive ${preview?.total_duplicate_rows || 0} duplicate job(s)?\n\n` +
      `Keeps the most complete/established row per item (certificates + calibration history + documents ` +
      `outrank raw age) and moves the rest — plus their orphan equipment/certificate records, if nothing else ` +
      `still references them — into a recoverable archive (never a hard delete).`
    )) return;
    setApplying(true);
    try {
      const r = await api.post("/migrate/wip-seed-duplicates/merge-all", null, { params: { apply: true } });
      setResult(r.data);
      setPreview(null);
      toast.success(`Archived ${r.data.archived_job_items} duplicate job(s), ${r.data.archived_equipment} orphan equipment record(s).`);
    } catch (e) { toast.error(apiErr(e)); } finally { setApplying(false); }
  };

  return (
    <div className="card pad" style={{ marginTop: 16 }}>
      <div className="sec-h">
        <h3>Fix WIP-seed duplicates (same item imported more than once)</h3>
        <Sparkles size={18} color="var(--gold-deep)" />
      </div>
      <p style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 14 }}>
        Re-uploading the same equipment CSV can spawn a brand new equipment/job record for items with no
        serial number. This only flags a cluster as a confirmed duplicate when rows share the same customer +
        delivery note + description AND a matching certificate number or serial/asset number — a shared
        description alone is never enough (distinct units of the same model on one DN are common and must
        never be merged). Anything without that confirmation is listed separately for manual review, and every
        removal is archived (recoverable), never hard-deleted.
      </p>
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
        <button className="btn ghost" onClick={doScan} disabled={scanning} data-testid="wip-dupes-scan-btn">
          {scanning ? <><RefreshCw size={14} className="spin" /> Scanning…</> : <><Search size={14} /> Preview duplicates</>}
        </button>
        {preview && (
          <button className="btn ghost" onClick={doExport} disabled={exporting} data-testid="wip-dupes-export-btn">
            {exporting ? <><RefreshCw size={14} className="spin" /> Exporting…</> : <><FileSpreadsheet size={14} /> Export to Excel</>}
          </button>
        )}
        {preview && preview.total_duplicate_rows > 0 && (
          <button className="btn gold" onClick={doApply} disabled={applying} data-testid="wip-dupes-apply-btn">
            {applying ? <><RefreshCw size={14} className="spin" /> Archiving…</> : <><Sparkles size={14} /> Archive duplicates</>}
          </button>
        )}
      </div>

      {preview && (
        <div className="card pad" style={{ background: "var(--card-2)", marginBottom: 14 }}>
          <span className="badge b-gold" style={{ marginRight: 8 }}><span className="bd" />{preview.total_clusters} item(s) affected</span>
          <span className="badge b-gold" style={{ marginRight: 8 }}><span className="bd" />{preview.total_duplicate_rows} duplicate row(s) to remove</span>
          {preview.ambiguous_group_rows > 0 && (
            <span className="badge b-red" data-testid="wip-dupes-ambiguous-badge">
              <span className="bd" />{preview.ambiguous_group_rows} row(s) need manual review (no matching CR/serial)
            </span>
          )}
        </div>
      )}

      {result && (
        <div className="card pad" style={{ background: "var(--card-2)" }}>
          <div className="badge b-green"><span className="bd" />Applied</div>
          <div className="mono" style={{ fontSize: 12, marginTop: 8 }}>
            Duplicate jobs archived: <b style={{ color: "var(--green)" }}>{result.archived_job_items}</b><br />
            Orphan equipment archived: <b style={{ color: "var(--green)" }}>{result.archived_equipment}</b><br />
            Orphan certificates archived: <b style={{ color: "var(--green)" }}>{result.archived_certificates}</b>
            {result.ambiguous_group_rows > 0 && (
              <><br />Left for manual review: <b style={{ color: "var(--red)" }}>{result.ambiguous_group_rows}</b></>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


// ============================================================
//  Cross-batch duplicates — broader scan across ALL currently-active
//  job_items regardless of which import mechanism created them (catches
//  duplicates the source-table-scoped wip-seed-duplicates tool can't see,
//  e.g. an older Caspio import row duplicating a newer re-import).
// ============================================================
function CrossBatchDuplicatesCard() {
  const [preview, setPreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [result, setResult] = useState(null);

  const doExport = async () => {
    setExporting(true);
    try {
      await downloadXlsx("/migrate/wip-duplicates-cross-batch/export.xlsx", "wip_duplicates_cross_batch.xlsx");
      toast.success("Duplicate review exported.");
    } catch (e) { toast.error(apiErr(e)); } finally { setExporting(false); }
  };

  const doScan = async () => {
    setScanning(true);
    setResult(null);
    try {
      const r = await api.get("/migrate/wip-duplicates-cross-batch");
      setPreview(r.data);
      if (r.data.total_duplicate_rows === 0) toast.success("No duplicates found.");
      else toast.success(`Found ${r.data.total_duplicate_rows} duplicate row(s) across ${r.data.total_clusters} item(s).`);
    } catch (e) { toast.error(apiErr(e)); } finally { setScanning(false); }
  };

  const doApply = async () => {
    if (!window.confirm(
      `Archive ${preview?.total_duplicate_rows || 0} duplicate job(s)?\n\n` +
      `Keeps the most complete/established row per item and moves the rest — plus their orphan equipment/` +
      `certificate records, if nothing else still references them — into a recoverable archive (never a hard delete).`
    )) return;
    setApplying(true);
    try {
      const r = await api.post("/migrate/wip-duplicates-cross-batch/merge-all", null, { params: { apply: true } });
      setResult(r.data);
      setPreview(null);
      toast.success(`Archived ${r.data.archived_job_items} duplicate job(s), ${r.data.archived_equipment} orphan equipment record(s).`);
    } catch (e) { toast.error(apiErr(e)); } finally { setApplying(false); }
  };

  return (
    <div className="card pad" style={{ marginTop: 16 }}>
      <div className="sec-h">
        <h3>Fix cross-batch duplicates (any import source, currently active only)</h3>
        <Sparkles size={18} color="var(--gold-deep)" />
      </div>
      <p style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 14 }}>
        Scans every currently-active job across ALL import mechanisms (not just the Caspio WIP-seed batch above) —
        catches an older import row duplicating a newer one. Same safety rule: a cluster is only ever confirmed
        when rows share a matching certificate number or serial/asset number; a shared description alone never
        qualifies, and unconfirmed groups are listed separately for manual review, never auto-merged.
      </p>
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
        <button className="btn ghost" onClick={doScan} disabled={scanning} data-testid="cross-batch-dupes-scan-btn">
          {scanning ? <><RefreshCw size={14} className="spin" /> Scanning…</> : <><Search size={14} /> Preview duplicates</>}
        </button>
        {preview && (
          <button className="btn ghost" onClick={doExport} disabled={exporting} data-testid="cross-batch-dupes-export-btn">
            {exporting ? <><RefreshCw size={14} className="spin" /> Exporting…</> : <><FileSpreadsheet size={14} /> Export to Excel</>}
          </button>
        )}
        {preview && preview.total_duplicate_rows > 0 && (
          <button className="btn gold" onClick={doApply} disabled={applying} data-testid="cross-batch-dupes-apply-btn">
            {applying ? <><RefreshCw size={14} className="spin" /> Archiving…</> : <><Sparkles size={14} /> Archive duplicates</>}
          </button>
        )}
      </div>

      {preview && (
        <div className="card pad" style={{ background: "var(--card-2)", marginBottom: 14 }}>
          <span className="badge b-gold" style={{ marginRight: 8 }}><span className="bd" />{preview.total_clusters} item(s) affected</span>
          <span className="badge b-gold" style={{ marginRight: 8 }}><span className="bd" />{preview.total_duplicate_rows} duplicate row(s) to remove</span>
          {preview.ambiguous_group_rows > 0 && (
            <span className="badge b-red" data-testid="cross-batch-dupes-ambiguous-badge">
              <span className="bd" />{preview.ambiguous_group_rows} row(s) need manual review (no matching CR/serial)
            </span>
          )}
        </div>
      )}

      {result && (
        <div className="card pad" style={{ background: "var(--card-2)" }}>
          <div className="badge b-green"><span className="bd" />Applied</div>
          <div className="mono" style={{ fontSize: 12, marginTop: 8 }}>
            Duplicate jobs archived: <b style={{ color: "var(--green)" }}>{result.archived_job_items}</b><br />
            Orphan equipment archived: <b style={{ color: "var(--green)" }}>{result.archived_equipment}</b><br />
            Orphan certificates archived: <b style={{ color: "var(--green)" }}>{result.archived_certificates}</b>
            {result.ambiguous_group_rows > 0 && (
              <><br />Left for manual review: <b style={{ color: "var(--red)" }}>{result.ambiguous_group_rows}</b></>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


// ============================================================
//  Technician dedupe — collapse duplicate employees by name
// ============================================================
function TechnicianDedupeCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);

  const scan = async () => {
    setLoading(true);
    try {
      const r = await api.get("/dedupe/technicians");
      setData(r.data);
      if (r.data.total_clusters === 0) toast.success("No duplicate technicians found.");
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  const mergeOne = async (cl) => {
    if (!window.confirm(
      `Merge ${cl.duplicates.length} duplicate(s) of "${cl.master.name}" into the master?\n\n` +
      `All linked job_items will be re-pointed to the master employee record, then the duplicate ` +
      `employee rows are deleted. Audited.`
    )) return;
    setBusy(cl.master.id);
    try {
      const r = await api.post("/dedupe/technicians/merge", {
        master_id: cl.master.id,
        duplicate_ids: cl.duplicates.map((d) => d.id),
      });
      toast.success(`${r.data.duplicates_deleted} dup(s) merged · ${r.data.job_items_moved} job_item(s) re-pointed`);
      await scan();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(null); }
  };

  return (
    <div className="card pad" style={{ marginTop: 16 }}>
      <div className="sec-h">
        <h3>Technician dedupe</h3>
        <GitMerge size={18} color="var(--gold-deep)" />
      </div>
      <p style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 14 }}>
        Groups <span className="mono">employees</span> with <span className="mono">role=technician</span> by normalised name (case-insensitive, whitespace-collapsed). Master = the record with the most linked <span className="mono">job_items</span>, tie-broken by oldest id. Merging re-points <span className="mono">job_items.technician_id</span> + denormalised <span className="mono">technician_name</span>, then deletes the duplicate rows. Audited.
      </p>

      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
        <button className="btn gold" onClick={scan} disabled={loading} data-testid="tech-dedupe-scan">
          {loading ? <><RefreshCw size={14} className="spin" /> Scanning…</> : <><Search size={14} /> Scan for duplicates</>}
        </button>
        {data && (
          <span className="t-mut" style={{ fontSize: 13 }}>
            <b>{data.total_clusters}</b> cluster(s) · <b style={{ color: data.total_duplicate_rows ? "var(--red)" : undefined }}>{data.total_duplicate_rows}</b> duplicate row(s)
          </span>
        )}
      </div>

      {data && data.total_clusters === 0 && (
        <div className="notice"><CheckCircle2 size={14} color="var(--green)" /><span>Technician list is clean.</span></div>
      )}

      {data?.clusters?.map((cl) => (
        <div key={cl.master.id} className="card pad" style={{ marginBottom: 10, background: "var(--card-2)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
            <div>
              <span className="t-strong">{cl.master.name}</span>
              <span className="t-mut" style={{ marginLeft: 8, fontSize: 12 }}>· {cl.size} rows · {cl.duplicates.length} to merge</span>
            </div>
            <button className="btn sm gold" onClick={() => mergeOne(cl)} disabled={busy === cl.master.id}
                    data-testid={`tech-merge-${cl.master.id}`}>
              <GitMerge size={12} /> {busy === cl.master.id ? "Merging…" : `Merge ${cl.duplicates.length}`}
            </button>
          </div>
          <div className="tbl-wrap">
            <table>
              <thead><tr><th style={{ width: 60 }}>Role</th><th>ID</th><th>Name</th><th>Job-items</th><th>Active</th></tr></thead>
              <tbody>
                <tr style={{ background: "rgba(212,175,55,.08)" }}>
                  <td><span className="badge b-gold"><span className="bd" />Master</span></td>
                  <td className="mono" style={{ fontSize: 11 }}>{cl.master.id.slice(-8)}</td>
                  <td className="t-strong">{cl.master.name}</td>
                  <td className="mono">{cl.master.job_item_count}</td>
                  <td>{cl.master.active ? "✓" : "—"}</td>
                </tr>
                {cl.duplicates.map((d) => (
                  <tr key={d.id}>
                    <td><span className="badge b-red"><span className="bd" />Dup</span></td>
                    <td className="mono" style={{ fontSize: 11 }}>{d.id.slice(-8)}</td>
                    <td>{d.name}</td>
                    <td className="mono">{d.job_item_count}</td>
                    <td>{d.active ? "✓" : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
